import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Edit,
  Eye,
  FileText,
  Filter,
  Loader,
  Mail,
  Package,
  Plus,
  Receipt,
  Save,
  Search,
  Trash2,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DateRangeFilter from '../components/DateRangeFilter.jsx';
import EditBookingBalanceStripe from '../components/EditBookingBalanceStripe.jsx';
import SingleDatePicker from '../components/SingleDatePicker.jsx';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import { parseLedgerSaldoFavorDetailLines } from '../utils/bookingLedgerCreditLines.js';
import { reconcileVehicleLinePrices } from '../utils/bookingPricingDisplay.js';
import {
  buildCreditAbonoMethodOptions,
  getCreditAbonoPaymentHints,
} from '../utils/creditAbonoPaymentHints.js';
import {
  computeCobrarSaldoPendingDraftTable,
  computeDraftCreditDownLines,
  computeDraftPriceBreakdown,
  computeQuipuSupplementOrientativeLines,
  getOfficialBookingPriceBreakdown,
  getPendingSupplementDraftExtraLines,
  isAccessoryDraftDirty,
  isDeliveryFormDirty,
  isScheduleDraftDirty,
  sumDraftAccessoryPrices,
  sumSavedAccessoryPrices,
} from '../utils/editBookingFinancialPreview.js';
import CrearReserva from './CrearReserva.jsx';
import './Reservas.css';

// Asegurar que CONTRACTS esté disponible
const CONTRACTS_ENDPOINT = API_ENDPOINTS.CONTRACTS || '/api/contracts';

/** Valor `datetime-local` en hora del navegador a partir de un ISO de la API. */
const isoToDatetimeLocalValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Divide/une un valor "yyyy-MM-ddTHH:mm" (datetime-local) en fecha y hora. */
const splitDatetimeLocal = (value) => ({
  date: value ? String(value).slice(0, 10) : '',
  time: value ? String(value).slice(11, 16) : '',
});
const joinDatetimeLocal = (date, time) => (date ? `${date}T${time || '09:00'}` : '');

const datetimeLocalValueToIso = (localStr) => {
  if (!localStr || !String(localStr).trim()) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

/** Teléfono para el formulario CrearReserva (prefijo + número local) a partir de una reserva cargada. */
const phoneFieldsFromBooking = (booking) => {
  const gp = booking?.guestPhonePrefix?.trim();
  const gn = booking?.guestPhone?.trim();
  if (gp || gn) {
    const prefix = gp ? (gp.startsWith('+') ? gp : `+${gp.replace(/^\+?/, '')}`) : '+34';
    return { customerPhonePrefix: prefix, customerPhone: gn || '' };
  }
  const raw = booking?.customer?.phone?.trim();
  if (!raw) return { customerPhonePrefix: '+34', customerPhone: '' };
  const m = raw.match(/^\+(\d{1,3})(?:\s*)(.*)$/);
  if (m) {
    const rest = (m[2] || '').replace(/\s+/g, ' ').trim();
    return { customerPhonePrefix: `+${m[1]}`, customerPhone: rest };
  }
  return { customerPhonePrefix: '+34', customerPhone: raw };
};

/** Solo datos de cliente para reutilizar en «Nueva reserva» (sin fechas, vehículos ni pago). */
const buildCustomerPrefillFromBooking = (booking) => {
  if (!booking) return null;
  const { customerPhonePrefix, customerPhone } = phoneFieldsFromBooking(booking);
  return {
    customerName: (booking.customer?.fullName || booking.guestName || '').trim(),
    customerEmail: (booking.customer?.email || booking.guestEmail || '').trim(),
    customerPhonePrefix,
    customerPhone,
    customerDni: (booking.customer?.dni || booking.guestDni || '').trim(),
    customerAddress: (booking.customerAddress || '').trim(),
  };
};

// Columna 'Tipo' oculta mientras solo haya reservas de bicicletas
const SHOW_BUSINESS_GROUP_COLUMN = false;

const Reservas = () => {
  const { user, authenticatedFetch } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastOpenEditNonceRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableScroll, setTableScroll] = useState({ canLeft: false, canRight: false });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    customerSearch: '',
    startDate: '',
    endDate: '',
    createdFrom: '',
    createdTo: '',
    paymentMethod: '',
    source: '',
    /**
     * Filtro único: cobro en reserva (collectionStatus) o abono en cancelación (refundStatus).
     * '' | SETTLED | OUTSTANDING | PENDING_REFUND | REFUNDED
     */
    financialFilter: ''
  });
  /** Filtro exacto por cliente al llegar desde «Clientes»: { id, fullName, email } */
  const [customerFilter, setCustomerFilter] = useState(() => location.state?.customerFilter || null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  /** Incrementar al abrir el modal para remontar CrearReserva y aplicar prefill limpio. */
  const [createReservaModalKey, setCreateReservaModalKey] = useState(0);
  /** Si no es null, CrearReserva rellena solo el bloque de cliente y deja el resto como reserva nueva. */
  const [createReservaCustomerPrefill, setCreateReservaCustomerPrefill] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Estados para reemplazo de bicicletas dentro de una reserva (mismo modelo)
  const [replacementOptionsByRentalId, setReplacementOptionsByRentalId] = useState({});
  const [loadingReplacementByRentalId, setLoadingReplacementByRentalId] = useState({});
  const [selectedReplacementVehicleByRentalId, setSelectedReplacementVehicleByRentalId] = useState({});
  const [changingBikeByRentalId, setChangingBikeByRentalId] = useState({});
  const [pendingBikeSizeChange, setPendingBikeSizeChange] = useState(null); // { bookingId, rentalId, newVehicleId, currentSize, newSize }
  const [showConfirmBikeSizeChangeModal, setShowConfirmBikeSizeChangeModal] = useState(false);

  /** Borrador de accesorios por línea (vehicleRental id) para PUT bike-lines */
  const [accessoryDraftByRentalId, setAccessoryDraftByRentalId] = useState({});
  /** Errores inline por bicicleta (edición): mostrar debajo de esa línea, no como error global. */
  const [editAccessoryInlineErrorByRentalId, setEditAccessoryInlineErrorByRentalId] = useState({});
  /** Snapshot de accesorios guardados en servidor al iniciar sesión de edición (para bloquear “quitar” en suplemento). */
  const [accessoryServerSnapshotByRentalId, setAccessoryServerSnapshotByRentalId] = useState({});
  const [accessoriesByModelEdit, setAccessoriesByModelEdit] = useState({});
  const [savingBikeLines, _setSavingBikeLines] = useState(false);
  const [offlinePayMethod, setOfflinePayMethod] = useState('CASH');
  const [creditAbonoLoading, setCreditAbonoLoading] = useState(false);
  /** Forma de pago del abono al cliente (alineada con cobros previos; recomendación = mayor importe). */
  const [creditAbonoPaymentMethod, setCreditAbonoPaymentMethod] = useState('NONE');
  const [supplementQuipuAmount, setSupplementQuipuAmount] = useState('');
  const [showConfirmManualPayWithQuipuModal, setShowConfirmManualPayWithQuipuModal] = useState(false);
  const [confirmManualPayLoading, setConfirmManualPayLoading] = useState(false);
  const [showConfirmApplyLinesAndDelivery, setShowConfirmApplyLinesAndDelivery] = useState(false);
  const [savingDeliveryApply, _setSavingDeliveryApply] = useState(false);
  const [savingApplyLinesAndDelivery, setSavingApplyLinesAndDelivery] = useState(false);
  /** Tras aplicar líneas o entrega: oferta de factura Quipu o aviso de abono */
  const [postApplyFinancialModal, setPostApplyFinancialModal] = useState(null);
  /** Vista previa del desglose Quipu (GET pending-supplement-preview) */
  const [pendingSupplementPreview, setPendingSupplementPreview] = useState(null);
  const [pendingSupplementPreviewLoading, setPendingSupplementPreviewLoading] = useState(false);
  const [showPartialSupplementAmount, setShowPartialSupplementAmount] = useState(false);
  const [showConfirmApplyLinesAndEmitQuipu, setShowConfirmApplyLinesAndEmitQuipu] = useState(false);
  const [savingApplyLinesAndEmitQuipu, setSavingApplyLinesAndEmitQuipu] = useState(false);
  const [editModalTab, setEditModalTab] = useState('EDIT'); // EDIT | FINANCIAL | LEDGER

  /** Cambio de fechas (PATCH /schedule): borrador datetime-local + acciones */
  const [scheduleDraftStart, setScheduleDraftStart] = useState('');
  const [scheduleDraftEnd, setScheduleDraftEnd] = useState('');
  const [closedDays, setClosedDays] = useState([]);

  // Días cerrados (gestionados por ADMIN en /dias-cerrados): no se pueden elegir al reprogramar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(buildApiUrl(API_ENDPOINTS.CLOSED_DAYS));
        const data = await res.json();
        if (!cancelled && data.success) setClosedDays((data.data || []).map((d) => d.date));
      } catch (e) {
        if (!cancelled) console.error('Error cargando días cerrados:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticatedFetch]);
  const [applyingSchedule, setApplyingSchedule] = useState(false);
  /** Añadir bicicleta a la reserva */
  const [addBikeList, setAddBikeList] = useState([]);
  const [addBikeLoading, setAddBikeLoading] = useState(false);
  const [selectedAddVehicleId, setSelectedAddVehicleId] = useState('');
  /** Búsqueda + desplegable para elegir bici entre muchas disponibles */
  const [addBikeSearchQuery, setAddBikeSearchQuery] = useState('');
  const [addBikeComboboxOpen, setAddBikeComboboxOpen] = useState(false);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [removingRentalId, setRemovingRentalId] = useState(null);
  // Eliminación definitiva de reserva (solo ADMIN)
  const [deleteTarget, setDeleteTarget] = useState(null); // { bookingId, preview, loading, error }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingBooking, setDeletingBooking] = useState(false);

  const addBikeListFiltered = useMemo(() => {
    const q = addBikeSearchQuery.trim().toLowerCase();
    if (!q) return addBikeList;
    return addBikeList.filter((v) => {
      const model = String(v._modelName || '').toLowerCase();
      const sn = String(v.serialNumber || '').toLowerCase();
      const id = String(v.id ?? '');
      const size = String(v.size || '').toLowerCase();
      return model.includes(q) || sn.includes(q) || id.includes(q) || size.includes(q);
    });
  }, [addBikeList, addBikeSearchQuery]);

  // Estados para estadísticas
  const [stats, setStats] = useState({});

  // Cargar reservas
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      // Agregar filtros solo si tienen valor
      if (filters.status) params.set('status', filters.status);
      if (filters.customerSearch?.trim()) params.set('customerSearch', filters.customerSearch.trim());
      if (customerFilter?.id) params.set('customerId', customerFilter.id);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
      if (filters.createdTo) params.set('createdTo', filters.createdTo);
      if (filters.paymentMethod) params.set('paymentMethod', filters.paymentMethod);
      if (filters.source === 'fareharbor') params.set('source', 'fareharbor');
      if (filters.financialFilter === 'SETTLED' || filters.financialFilter === 'OUTSTANDING') {
        params.set('collectionStatus', filters.financialFilter);
      } else if (filters.financialFilter === 'PENDING_REFUND' || filters.financialFilter === 'REFUNDED') {
        params.set('refundStatus', filters.financialFilter);
      }

      // Si el usuario es AGENCY, sobrescribir filtros con los de agencia
      if (user?.role === 'AGENCY' && user?.agencyCode) {
        params.set('agencyCode', user.agencyCode);
        params.set('paymentMethod', 'COLABORADOR'); // Solo reservas con método COLABORADOR
      }

      const response = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.BOOKINGS)}?${params}`);
      const data = await response.json();

      if (data.success) {
        setBookings(data.data.bookings);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages
        }));
        setStats(data.data.stats);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Error al cargar las reservas');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar estadísticas
  const fetchStats = async () => {
    try {
      let url = `${buildApiUrl(API_ENDPOINTS.BOOKINGS)}/stats`;
      
      // Si el usuario es AGENCY, agregar filtro por agencia
      if (user?.role === 'AGENCY' && user?.agencyCode) {
        url += `?agencyCode=${user.agencyCode}`;
      }
      
      const response = await authenticatedFetch(url);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [pagination.page, filters, customerFilter]);

  // Botones de scroll horizontal de la tabla: habilitar según la posición
  const updateTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setTableScroll({
      canLeft: el.scrollLeft > 1,
      canRight: el.scrollLeft < maxScroll - 1,
    });
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    updateTableScroll();
    const observer = new ResizeObserver(updateTableScroll);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [bookings, loading]);

  const scrollTable = (direction) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: 'smooth' });
  };

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Manejar filtros
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCreatedRangeChange = (createdFrom, createdTo) => {
    setFilters(prev => ({ ...prev, createdFrom, createdTo }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRentalRangeChange = (startDate, endDate) => {
    setFilters(prev => ({ ...prev, startDate, endDate }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setFilters({
      status: '',
      customerSearch: '',
      startDate: '',
      endDate: '',
      createdFrom: '',
      createdTo: '',
      paymentMethod: '',
      source: '',
      financialFilter: ''
    });
    setCustomerFilter(null);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearCustomerFilter = () => {
    setCustomerFilter(null);
    setPagination(prev => ({ ...prev, page: 1 }));
    navigate('.', { replace: true, state: {} });
  };

  // Estados para contratos
  const [bookingContracts, setBookingContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // Cargar contratos de una reserva
  const loadBookingContracts = async (bookingId) => {
    try {
      setLoadingContracts(true);
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.CONTRACTS}/${bookingId}`));
      const data = await response.json();
      
      if (data.success) {
        setBookingContracts(data.data || []);
      } else {
        setBookingContracts([]);
      }
    } catch (error) {
      console.error('Error al cargar contratos:', error);
      setBookingContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  };

  // Descargar contrato firmado guardado
  const downloadSignedContract = async (contract) => {
    try {
      if (!contract || !contract.filePath) {
        setError('El contrato no tiene una ruta de archivo válida');
        return;
      }

      const pathParts = contract.filePath.split('/');
      const filename = pathParts[pathParts.length - 1];
      const bookingId = pathParts[pathParts.length - 2]?.replace('booking_', '');
      const month = pathParts[pathParts.length - 3];
      const year = pathParts[pathParts.length - 4];

      let downloadUrl;
      if (year && month && bookingId && filename) {
        downloadUrl = `${API_ENDPOINTS.CONTRACTS}/download/${year}/${month}/${bookingId}/${filename}`;
      } else {
        downloadUrl = `${API_ENDPOINTS.CONTRACTS}/download/${filename}`;
      }

      const response = await authenticatedFetch(buildApiUrl(downloadUrl));
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `contract_${contract.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        setError(data.message || 'Error al descargar el contrato');
      }
    } catch (error) {
      setError('Error al descargar el contrato');
      console.error('Error:', error);
    }
  };

  // Visualizar contrato firmado en nueva pestaña
  const viewSignedContract = async (contract) => {
    try {
      if (!contract || !contract.filePath) {
        setError('El contrato no tiene una ruta de archivo válida');
        return;
      }

      const pathParts = contract.filePath.split('/');
      const filename = pathParts[pathParts.length - 1];
      const bookingId = pathParts[pathParts.length - 2]?.replace('booking_', '');
      const month = pathParts[pathParts.length - 3];
      const year = pathParts[pathParts.length - 4];

      let viewUrl;
      if (year && month && bookingId && filename) {
        viewUrl = `${API_ENDPOINTS.CONTRACTS}/download/${year}/${month}/${bookingId}/${filename}`;
      } else {
        viewUrl = `${API_ENDPOINTS.CONTRACTS}/download/${filename}`;
      }

      // Usar authenticatedFetch para incluir el token de autenticación
      const response = await authenticatedFetch(buildApiUrl(viewUrl));
      
      if (response.ok) {
        // Verificar que el contenido sea un PDF
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          // Limpiar el URL después de un tiempo para liberar memoria
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } else {
          // Si no es PDF, puede ser un JSON de error
          const data = await response.json();
          setError(data.message || 'Error al visualizar el contrato');
        }
      } else {
        // Intentar obtener el mensaje de error
        try {
          const data = await response.json();
          setError(data.message || 'Error al visualizar el contrato');
        } catch {
          setError(`Error ${response.status}: No se pudo visualizar el contrato`);
        }
      }
    } catch (error) {
      setError('Error al visualizar el contrato');
      console.error('Error:', error);
    }
  };

  const openDeleteBookingModal = async (bookingId) => {
    setDeleteConfirmText('');
    setDeleteTarget({ bookingId, preview: null, loading: true, error: null });
    try {
      const res = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}/delete-preview`));
      const data = await res.json();
      if (!data.success) {
        setDeleteTarget({ bookingId, preview: null, loading: false, error: data.message || 'No se pudo cargar la información de la reserva' });
        return;
      }
      setDeleteTarget({ bookingId, preview: data.data, loading: false, error: null });
    } catch (e) {
      console.error(e);
      setDeleteTarget({ bookingId, preview: null, loading: false, error: 'Error de conexión al cargar la reserva' });
    }
  };

  const closeDeleteBookingModal = () => {
    if (deletingBooking) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const handleConfirmDeleteBooking = async () => {
    if (!deleteTarget?.preview) return;
    try {
      setDeletingBooking(true);
      const res = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${deleteTarget.bookingId}`), {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!data.success) {
        setDeleteTarget((prev) => ({ ...prev, error: data.message || 'No se pudo eliminar la reserva' }));
        return;
      }
      if (selectedBooking?.id === deleteTarget.bookingId) {
        setShowViewModal(false);
        setShowEditModal(false);
        setSelectedBooking(null);
      }
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchBookings();
    } catch (e) {
      console.error(e);
      setDeleteTarget((prev) => ({ ...prev, error: 'Error de conexión al eliminar la reserva' }));
    } finally {
      setDeletingBooking(false);
    }
  };

  // Ver detalles de reserva
  const handleViewBooking = async (id) => {
    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${id}`));
      const data = await response.json();
      
      if (data.success) {
        setSelectedBooking({ ...data.data, financials: data.financials });
        setShowViewModal(true);
        // Cargar contratos si no vienen en la respuesta
        if (!data.data.contracts || data.data.contracts.length === 0) {
          loadBookingContracts(id);
        }
      }
    } catch (error) {
      console.error('Error al cargar reserva:', error);
    }
  };

  // Editar reserva
  const handleEditBooking = async (booking) => {
    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${booking.id}`));
      const data = await response.json();
      const fullBooking = data?.success ? data.data : booking;
      const financials = data?.success ? data.financials : undefined;
      initializeEditSessionFromBooking(fullBooking, financials);
      setEditModalTab('EDIT');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error al cargar reserva completa para edición:', error);
      initializeEditSessionFromBooking(booking, booking?.financials);
      setEditModalTab('EDIT');
      setShowEditModal(true);
    }
  };

  // Abrir edición al llegar desde «Preparación del día» (u otra vista) con state dedicado
  useEffect(() => {
    const id = location.state?.openEditBookingId;
    const nonce = location.state?.openEditNonce;
    if (!id || nonce == null || user?.role === 'AGENCY') return;
    if (lastOpenEditNonceRef.current === nonce) return;
    lastOpenEditNonceRef.current = nonce;
    navigate('.', { replace: true, state: {} });
    void handleEditBooking({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo reaccionar al state de navegación y rol
  }, [location.state?.openEditBookingId, location.state?.openEditNonce, user?.role]);

  /** Cierra el modal de edición y los diálogos asociados (aplicar líneas / entrega / Quipu). */
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditModalTab('EDIT');
    setShowConfirmApplyLinesAndDelivery(false);
    setShowConfirmApplyLinesAndEmitQuipu(false);
    setShowConfirmManualPayWithQuipuModal(false);
    setConfirmManualPayLoading(false);
    setPostApplyFinancialModal(null);
    setPendingSupplementPreview(null);
    setPendingSupplementPreviewLoading(false);
    setShowPartialSupplementAmount(false);
    setScheduleDraftStart('');
    setScheduleDraftEnd('');
    setAddBikeList([]);
    setSelectedAddVehicleId('');
    setAddBikeSearchQuery('');
    setAddBikeComboboxOpen(false);
    setRemovingRentalId(null);
    setEditAccessoryInlineErrorByRentalId({});
  };

  const loadReplacementVehicles = async (bookingId, rentalId) => {
    try {
      setLoadingReplacementByRentalId(prev => ({ ...prev, [rentalId]: true }));
      setError(null);
      const endpoint = `${API_ENDPOINTS.BOOKINGS}/${bookingId}/vehicle-rentals/${rentalId}/available-vehicles`;
      const response = await authenticatedFetch(buildApiUrl(endpoint));
      const data = await response.json();
      if (data.success) {
        setReplacementOptionsByRentalId(prev => ({ ...prev, [rentalId]: data.data || [] }));
      } else {
        setError(data.message || 'No se pudieron cargar las bicicletas disponibles');
      }
    } catch (e) {
      console.error('Error cargando reemplazos:', e);
      setError('Error al cargar las bicicletas disponibles');
    } finally {
      setLoadingReplacementByRentalId(prev => ({ ...prev, [rentalId]: false }));
    }
  };

  const applyReplacementVehicle = async (bookingId, rentalId) => {
    const newVehicleId = selectedReplacementVehicleByRentalId[rentalId];
    if (!newVehicleId) return;

    try {
      setChangingBikeByRentalId(prev => ({ ...prev, [rentalId]: true }));
      setError(null);
      const endpoint = `${API_ENDPOINTS.BOOKINGS}/${bookingId}/vehicle-rentals/${rentalId}/change-vehicle`;
      const response = await authenticatedFetch(buildApiUrl(endpoint), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newVehicleId: parseInt(newVehicleId) })
      });
      const data = await response.json();
      if (data.success) {
        // Actualizar reserva en el modal con la data devuelta por backend
        if (data.data?.booking) {
          setSelectedBooking(data.data.booking);
        }
        // Limpiar selección y refrescar lista
        setSelectedReplacementVehicleByRentalId(prev => ({ ...prev, [rentalId]: '' }));
        fetchBookings();
        fetchStats();
      } else {
        setError(data.message || 'No se pudo cambiar la bicicleta');
      }
    } catch (e) {
      console.error('Error aplicando reemplazo:', e);
      setError('Error al cambiar la bicicleta');
    } finally {
      setChangingBikeByRentalId(prev => ({ ...prev, [rentalId]: false }));
    }
  };

  const requestApplyReplacementVehicle = (bookingId, rentalId) => {
    const newVehicleId = selectedReplacementVehicleByRentalId[rentalId];
    if (!newVehicleId) return;

    const rental = selectedBooking?.vehicleRentals?.find(r => r.id === rentalId);
    const currentSize = rental?.vehicle?.size;
    const options = replacementOptionsByRentalId[rentalId] || [];
    const picked = options.find(v => v.id === parseInt(newVehicleId));
    const newSize = picked?.size;

    if (currentSize && newSize && String(currentSize) !== String(newSize)) {
      setPendingBikeSizeChange({
        bookingId,
        rentalId,
        newVehicleId: parseInt(newVehicleId),
        currentSize: String(currentSize),
        newSize: String(newSize)
      });
      setShowConfirmBikeSizeChangeModal(true);
      return;
    }

    applyReplacementVehicle(bookingId, rentalId);
  };

  const loadAddBikeVehicles = async () => {
    if (!selectedBooking?.id || !selectedBooking.startDateTime || !selectedBooking.endDateTime) return;
    try {
      setAddBikeLoading(true);
      setError(null);
      const qs = new URLSearchParams({
        startDateTime: selectedBooking.startDateTime,
        endDateTime: selectedBooking.endDateTime
      });
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/available-vehicles?${qs}`)
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudieron cargar bicicletas disponibles');
        setAddBikeList([]);
        return;
      }
      const groups = Array.isArray(data.data) ? data.data : [];
      const usedIds = new Set((selectedBooking.vehicleRentals || []).map((r) => r.vehicleId));
      const flat = [];
      for (const group of groups) {
        const list = group?.vehicles;
        if (!Array.isArray(list)) continue;
        for (const v of list) {
          if (v?.id != null && !usedIds.has(v.id)) {
            flat.push({
              ...v,
              _modelName: group.name,
              _modelId: group.id
            });
          }
        }
      }
      const seen = new Set();
      const uniq = [];
      for (const v of flat) {
        if (seen.has(v.id)) continue;
        seen.add(v.id);
        uniq.push(v);
      }
      setAddBikeList(uniq);
      setAddBikeSearchQuery('');
      setAddBikeComboboxOpen(false);
      setSelectedAddVehicleId((prev) => {
        if (!prev) return '';
        return uniq.some((v) => String(v.id) === String(prev)) ? prev : '';
      });
      if (uniq.length === 0) {
        setError('No hay más bicicletas libres en este periodo (o ya están todas en la reserva).');
      }
    } catch (e) {
      console.error(e);
      setError('Error al cargar bicicletas disponibles');
      setAddBikeList([]);
    } finally {
      setAddBikeLoading(false);
    }
  };

  const addVehicleToBookingRequest = async () => {
    const vid = parseInt(selectedAddVehicleId, 10);
    if (!selectedBooking?.id || Number.isNaN(vid)) return;
    const before = financialSnapshotFromBooking(selectedBooking);
    try {
      setAddingVehicle(true);
      setError(null);
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/vehicle-rentals`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vehicleId: vid })
        }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudo añadir la bicicleta');
        return;
      }
      const merged = { ...data.data, financials: data.financials };
      setSelectedBooking(merged);
      hydrateAccessoryDraftFromBooking(data.data);
      setScheduleDraftStart(isoToDatetimeLocalValue(data.data.startDateTime));
      setScheduleDraftEnd(isoToDatetimeLocalValue(data.data.endDateTime));
      setSelectedAddVehicleId('');
      setAddBikeSearchQuery('');
      setAddBikeComboboxOpen(false);
      setAddBikeList((prev) => prev.filter((v) => v.id !== vid));
      fetchBookings();
      openPostApplyFinancialIfNeeded(before, financialSnapshotFromBooking(merged), merged);
    } catch (e) {
      console.error(e);
      setError('Error al añadir la bicicleta');
    } finally {
      setAddingVehicle(false);
    }
  };

  const removeVehicleRentalRequest = async (rentalId) => {
    if (!selectedBooking?.id || !rentalId) return;
    if (
      !window.confirm(
        '¿Quitar esta bicicleta de la reserva? Se eliminarán sus accesorios y bloques de disponibilidad asociados.'
      )
    ) {
      return;
    }
    const before = financialSnapshotFromBooking(selectedBooking);
    try {
      setRemovingRentalId(rentalId);
      setError(null);
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/vehicle-rentals/${rentalId}`),
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudo quitar la bicicleta');
        return;
      }
      const merged = { ...data.data, financials: data.financials };
      setSelectedBooking(merged);
      hydrateAccessoryDraftFromBooking(data.data);
      setScheduleDraftStart(isoToDatetimeLocalValue(data.data.startDateTime));
      setScheduleDraftEnd(isoToDatetimeLocalValue(data.data.endDateTime));
      fetchBookings();
      openPostApplyFinancialIfNeeded(before, financialSnapshotFromBooking(merged), merged);
    } catch (e) {
      console.error(e);
      setError('Error al quitar la bicicleta');
    } finally {
      setRemovingRentalId(null);
    }
  };

  const applyScheduleChangeRequest = async () => {
    if (!selectedBooking?.id) return;
    const startIso = datetimeLocalValueToIso(scheduleDraftStart);
    const endIso = datetimeLocalValueToIso(scheduleDraftEnd);
    if (!startIso || !endIso) {
      setError('Indica inicio y fin válidos.');
      return;
    }
    if (new Date(startIso) >= new Date(endIso)) {
      setError('La fecha de inicio debe ser anterior a la de fin.');
      return;
    }
    const before = financialSnapshotFromBooking(selectedBooking);
    try {
      setApplyingSchedule(true);
      setError(null);
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/schedule`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDateTime: startIso, endDateTime: endIso })
        }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'No se pudieron actualizar las fechas');
        return;
      }
      const merged = { ...data.data, financials: data.financials };
      setSelectedBooking(merged);
      hydrateAccessoryDraftFromBooking(data.data);
      setScheduleDraftStart(isoToDatetimeLocalValue(data.data.startDateTime));
      setScheduleDraftEnd(isoToDatetimeLocalValue(data.data.endDateTime));
      setReplacementOptionsByRentalId({});
      setSelectedReplacementVehicleByRentalId({});
      fetchBookings();
      openPostApplyFinancialIfNeeded(before, financialSnapshotFromBooking(merged), merged);
    } catch (e) {
      console.error(e);
      setError('Error al actualizar las fechas');
    } finally {
      setApplyingSchedule(false);
    }
  };

  // Saber si el pago principal es Stripe (reembolso automático)
  const isStripePayment = (booking) => {
    const payment = getMainPayment(booking);
    const method = payment?.paymentMethod ?? payment?.method;
    return method === 'STRIPE' || method === 'CARD' || method === 'TARJETA';
  };

  const formatEur = (v) =>
    new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

  const computeDurationDays = (start, end) => {
    if (!start || !end) return 1;
    const a = new Date(start);
    const b = new Date(end);
    const d = Math.ceil((b - a) / 86400000);
    return Math.max(1, d);
  };

  const resolveRentalAccessoryId = (a) => {
    if (a == null) return null;
    const raw = a.accessoryId ?? a.accessory?.id;
    if (raw === undefined || raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const hydrateAccessoryDraftFromBooking = (b) => {
    if (!b?.vehicleRentals) return;
    const next = {};
    for (const vr of b.vehicleRentals) {
      next[vr.id] = (vr.accessories || [])
        .map((a) => ({
          accessoryId: resolveRentalAccessoryId(a),
          price: Number(a.finalPrice) || 0
        }))
        .filter((row) => row.accessoryId != null);
    }
    setAccessoryDraftByRentalId(next);
  };

  const snapshotAccessoriesFromBooking = (b) => {
    if (!b?.vehicleRentals) return {};
    const snap = {};
    for (const vr of b.vehicleRentals) {
      snap[vr.id] = (vr.accessories || [])
        .map((a) => ({
          accessoryId: resolveRentalAccessoryId(a),
          price: Number(a.finalPrice) || 0,
        }))
        .filter((row) => row.accessoryId != null);
    }
    return snap;
  };

  const isBookingFullyPaid = (booking) => {
    const fin = booking?.financials;
    if (!fin) return false;
    const due = Number(fin.balanceDue ?? 0);
    const nextPay = Number(fin.nextExpectedPaymentAmount ?? 0);
    return due <= 0.02 && nextPay <= 0.02;
  };

  const removedServerAccessoryBlockMessage = (booking, serverSnap, draftSnap) => {
    // Regla: en el flujo de “suplemento” queremos que esta operación sea solo incrementos.
    // Si se necesita quitar un accesorio ya guardado, debe hacerse como operación separada (bajada/abono).
    // Excepción: si la reserva está totalmente pagada, permitir quitar (bajada) y tratarlo como operación de ajuste.
    if (isBookingFullyPaid(booking)) return null;
    const rentals = booking?.vehicleRentals || [];
    for (const vr of rentals) {
      const base = serverSnap?.[vr.id] || [];
      if (!base.length) continue;
      const draft = draftSnap?.[vr.id] || [];
      const draftIds = new Set(draft.map((x) => Number(x.accessoryId)));
      for (const a of base) {
        if (!draftIds.has(Number(a.accessoryId))) {
          // Permitir sustitución de seguro: quitar un seguro guardado pero mantener otro seguro seleccionado
          // en la misma bicicleta (swap de seguro), aunque haya pendiente.
          const removedSaved = (vr.accessories || []).find(
            (x) => Number(resolveRentalAccessoryId(x)) === Number(a.accessoryId)
          );
          const removedCategory =
            removedSaved?.accessory?.category ?? resolveDraftAccessoryCategory(vr.id, a.accessoryId);
          const removedIsInsurance =
            removedCategory === 'INSURANCE' ||
            String(removedCategory || '').toUpperCase() === 'INSURANCE';
          if (removedIsInsurance) {
            const draftHasAnyInsurance = draft.some((x) => {
              const cat = resolveDraftAccessoryCategory(vr.id, x.accessoryId);
              return cat === 'INSURANCE' || String(cat || '').toUpperCase() === 'INSURANCE';
            });
            if (draftHasAnyInsurance) continue;
          }
          return {
            rentalId: vr.id,
            message:
              'En un cobro de suplemento solo se permite añadir accesorios. Para quitar un accesorio de una bicicleta existente, inicia esa operación aparte (bajada/abono) y aplica los cambios sin añadir incrementos.',
          };
        }
      }
    }
    return null;
  };

  /**
   * Sincroniza el modal con los datos de la reserva (p. ej. al abrir o tras refrescar desde el servidor).
   * No cierra el modal ni cambia de pestaña salvo que el llamador lo haga.
   */
  const initializeEditSessionFromBooking = (fullBooking, financials) => {
    const merged =
      financials !== undefined && financials !== null
        ? { ...fullBooking, financials }
        : fullBooking;
    setSelectedBooking(merged);
    setScheduleDraftStart(isoToDatetimeLocalValue(fullBooking.startDateTime));
    setScheduleDraftEnd(isoToDatetimeLocalValue(fullBooking.endDateTime));
    setAddBikeList([]);
    setSelectedAddVehicleId('');
    setAddBikeSearchQuery('');
    setAddBikeComboboxOpen(false);
    setEditForm({
      status: fullBooking.status,
      deliveryType: fullBooking.deliveryType === 'HOTEL' ? 'DELIVERY' : fullBooking.deliveryType,
      deliveryCost:
        fullBooking.deliveryCost != null && fullBooking.deliveryCost !== ''
          ? String(fullBooking.deliveryCost)
          : '0',
      hotelAddress: fullBooking.hotelAddress || '',
      customerName: fullBooking.customer?.fullName || fullBooking.guestName || '',
      customerEmail: fullBooking.customer?.email || fullBooking.guestEmail || '',
      customerPhone: fullBooking.customer?.phone || fullBooking.guestPhone || '',
      customerPhonePrefix: fullBooking.customer?.phonePrefix || fullBooking.guestPhonePrefix || '',
      customerDni: fullBooking.customer?.dni || fullBooking.guestDni || '',
      notes: fullBooking.notes || '',
      refundStatus: fullBooking.refundStatus || 'PENDING_REFUND',
      noShow: !!fullBooking.noShow
    });
    setReplacementOptionsByRentalId({});
    setLoadingReplacementByRentalId({});
    setSelectedReplacementVehicleByRentalId({});
    setChangingBikeByRentalId({});
    const abonoHints = getCreditAbonoPaymentHints(fullBooking.payments);
    const rec = abonoHints.recommended?.method;
    const pick =
      rec && ['STRIPE', 'TPV', 'CASH', 'BIZUM', 'BANK_TRANSFER', 'NONE'].includes(rec)
        ? rec
        : 'NONE';
    setCreditAbonoPaymentMethod(pick);
    setSupplementQuipuAmount('');
    setShowPartialSupplementAmount(false);
    setPendingSupplementPreview(null);
    hydrateAccessoryDraftFromBooking(fullBooking);
    setAccessoryServerSnapshotByRentalId(snapshotAccessoriesFromBooking(fullBooking));
    setEditAccessoryInlineErrorByRentalId({});
    setError(null);
  };

  const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const effectiveDeliveryCostFromBooking = (b) => {
    const t = b?.deliveryType;
    if (t === 'DELIVERY' || t === 'HOTEL') return Number(b?.deliveryCost) || 0;
    return 0;
  };

  /** Totales guardados en servidor (para comparar antes/después de cambios económicos). */
  const financialSnapshotFromBooking = (b) => ({
    balanceDue: Number(b?.financials?.balanceDue ?? 0),
    paidTotal: Number(b?.financials?.paidTotal ?? 0),
    total: Number(b?.total ?? 0),
    vehiclesTotal: Number(b?.vehiclesTotal) || 0,
    accessoriesTotal: Number(b?.accessoriesTotal) || 0,
    insuranceTotal: Number(b?.insuranceTotal) || 0,
    deliveryCost: Number(b?.deliveryCost) || 0,
    deliveryType: b?.deliveryType,
    effectiveDeliveryCost: effectiveDeliveryCostFromBooking(b),
  });

  const computePriceDeltaBreakdown = (before, after) => ({
    deltaVehicles: roundMoney((after?.vehiclesTotal ?? 0) - (before?.vehiclesTotal ?? 0)),
    deltaAccessories: roundMoney((after?.accessoriesTotal ?? 0) - (before?.accessoriesTotal ?? 0)),
    deltaInsurance: roundMoney((after?.insuranceTotal ?? 0) - (before?.insuranceTotal ?? 0)),
    deltaDelivery: roundMoney((after?.effectiveDeliveryCost ?? 0) - (before?.effectiveDeliveryCost ?? 0)),
    deltaTotal: roundMoney((after?.total ?? 0) - (before?.total ?? 0)),
  });

  /** Pagos ya cobrados con factura Quipu (anticipo o pago normal). */
  const bookingHasPaidInvoicedQuipu = (booking) =>
    (booking?.payments || []).some((p) => {
      if (p.status !== 'PAID') return false;
      if (!p.quipuInvoiceId) return false;
      const mt = p.movementType || 'PAYMENT';
      return mt === 'PAYMENT' || mt === 'ADVANCE';
    });

  /**
   * Hay un cobro/factura pendiente asociado a la reserva (típicamente “suplemento pendiente” en Quipu):
   * mientras exista, no permitimos bajar accesorios/entrega porque netearía con algo aún no liquidado.
   */
  const bookingHasPendingInvoiceLikePayment = (booking) =>
    (booking?.payments || []).some((p) => {
      if (p.status !== 'PENDING') return false;
      // Si está ligado a Quipu o marcado para facturar, lo tratamos como pendiente “fiscal/contable”
      if (p.quipuInvoiceId) return true;
      if (p.requiresInvoice) return true;
      const mt = p.movementType || 'PAYMENT';
      return mt === 'PAYMENT' || mt === 'ADVANCE';
    });

  /** Hay bajada y subida en distintos conceptos → no conviene una sola factura “neto” si ya hubo facturas. */
  const hasOppositePriceDeltas = (b) => {
    const down =
      b.deltaVehicles < -0.02 ||
      b.deltaAccessories < -0.02 ||
      b.deltaInsurance < -0.02 ||
      b.deltaDelivery < -0.02;
    const up =
      b.deltaVehicles > 0.02 ||
      b.deltaAccessories > 0.02 ||
      b.deltaInsurance > 0.02 ||
      b.deltaDelivery > 0.02;
    return down && up;
  };

  const QUIPU_MIXED_INTENT_MSG =
    'Ya hay facturas en Quipu en esta reserva: no puedes aplicar en un solo paso una subida y una bajada de importe en accesorios o entrega (por ejemplo añadir entrega y quitar un accesorio). Hazlo en dos ediciones: primero solo el abono o rectificación por lo que quitas, o solo el suplemento por lo que añades; después, en otra aplicación, la otra operación.';

  const GENERIC_MIXED_INTENT_MSG =
    'No puedes mezclar en el mismo borrador un suplemento (entrega o más accesorios) y una bajada (quitar accesorios o reducir entrega): el total se netearía de forma incorrecta. Si empezaste un cobro extra, aplica solo eso; en otra edición, la bajada o el abono.';

  const mixedIntentBlockMessage = (booking) =>
    bookingHasPaidInvoicedQuipu(booking) ? QUIPU_MIXED_INTENT_MSG : GENERIC_MIXED_INTENT_MSG;

  const BALANCE_DUE_NO_LOWER_MSG =
    'Hay saldo pendiente de cobro en esta reserva (p. ej. suplemento de entrega sin liquidar). No puedes bajar el total quitando accesorios ni reduciendo la entrega hasta registrar ese cobro. No mezcles un cobro pendiente con una bajada tipo abono.';

  const sumSavedAccessoryTotal = sumSavedAccessoryPrices;
  const sumDraftAccessoryTotal = (_booking, accessoryDraftByRentalId) =>
    sumDraftAccessoryPrices(accessoryDraftByRentalId);

  const effectiveDeliveryFromEditForm = (form) => {
    const t = form?.deliveryType;
    if (t === 'PICKUP') return 0;
    const n = parseFloat(String(form?.deliveryCost ?? '0').replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? roundMoney(n) : 0;
  };

  /**
   * Subida y bajada a la vez en accesorios o entrega (borrador vs guardado): no permitir
   * (neteo incorrecto p. ej. +60 € entrega y −2 € accesorio → 58 €; y con Quipu, requisito fiscal).
   */
  const mixedAccessoryDeliveryIntent = (booking, editForm, accessoryDraftByRentalId) => {
    const savedAcc = sumSavedAccessoryTotal(booking);
    const draftAcc = sumDraftAccessoryTotal(booking, accessoryDraftByRentalId);
    const dAcc = roundMoney(draftAcc - savedAcc);
    const savedDel = effectiveDeliveryCostFromBooking(booking);
    const draftDel = effectiveDeliveryFromEditForm(editForm);
    const dDel = roundMoney(draftDel - savedDel);
    const eps = 0.02;
    const up = dAcc > eps || dDel > eps;
    const down = dAcc < -eps || dDel < -eps;
    if (!up || !down) return { blocked: false, dAcc, dDel };
    return {
      blocked: true,
      dAcc,
      dDel,
      savedAcc,
      draftAcc,
      savedDel,
      draftDel,
    };
  };

  /** Con cobro pendiente: no bajar total por accesorios/entrega (evita netear suplemento con “abono”). */
  const pendingDueBlocksLower = (booking, form, draft) => {
    // Importante: el “saldo pendiente” por sí solo NO debe bloquear ediciones del borrador;
    // solo bloqueamos si hay un cobro/factura en estado PENDING (p. ej. suplemento Quipu emitido y aún no liquidado).
    if (!bookingHasPendingInvoiceLikePayment(booking)) return false;
    const savedAcc = sumSavedAccessoryTotal(booking);
    const draftAcc = sumDraftAccessoryTotal(booking, draft);
    const dAcc = roundMoney(draftAcc - savedAcc);
    const savedDel = effectiveDeliveryCostFromBooking(booking);
    const draftDel = effectiveDeliveryFromEditForm(form);
    const dDel = roundMoney(draftDel - savedDel);
    const eps = 0.02;
    return dAcc < -eps || dDel < -eps;
  };

  /** Entrega a domicilio: dirección obligatoria en el borrador de edición. */
  const deliveryStreetRequiredMessage = (form) => {
    const t = String(form?.deliveryType || '').toUpperCase();
    if (t !== 'DELIVERY' && t !== 'HOTEL') return '';
    const addr = String(form?.hotelAddress ?? '').trim();
    if (!addr) {
      return 'La dirección de entrega a domicilio es obligatoria (calle, número y localidad).';
    }
    return '';
  };

  /** Mensaje si no se debe permitir el borrador / aplicar (mezcla subida+bajada, o bajada con pendiente de cobro). */
  const priceEditBlockMessage = (booking, form, draft) => {
    const mix = mixedAccessoryDeliveryIntent(booking, form, draft);
    if (mix.blocked) return mixedIntentBlockMessage(booking);
    if (pendingDueBlocksLower(booking, form, draft)) return BALANCE_DUE_NO_LOWER_MSG;
    const addrMsg = deliveryStreetRequiredMessage(form);
    if (addrMsg) return addrMsg;
    return '';
  };

  const priceEditBlocked = (booking, form, draft) => priceEditBlockMessage(booking, form, draft) !== '';

  const tryOpenConfirmApplyLinesAndDelivery = () => {
    if (user?.role === 'AGENCY' || !selectedBooking) return;
    const blockMsg = priceEditBlockMessage(selectedBooking, editForm, accessoryDraftByRentalId);
    if (blockMsg) {
      setError(blockMsg);
      return;
    }
    setError(null);
    setShowConfirmApplyLinesAndDelivery(true);
  };

  const tryOpenConfirmApplyLinesAndEmitQuipu = () => {
    if (user?.role === 'AGENCY' || !selectedBooking) return;
    const blockMsg = priceEditBlockMessage(selectedBooking, editForm, accessoryDraftByRentalId);
    if (blockMsg) {
      setError(blockMsg);
      return;
    }
    setError(null);
    setShowConfirmApplyLinesAndEmitQuipu(true);
  };

  const buildDeliveryPayloadFromEditForm = (form) => {
    const payload = {};
    if (form.deliveryType === 'PICKUP') {
      payload.deliveryType = 'PICKUP';
      payload.deliveryCost = 0;
      payload.hotelAddress = '';
    } else {
      payload.deliveryType = 'DELIVERY';
      const n = parseFloat(String(form.deliveryCost ?? '0').replace(',', '.'));
      payload.deliveryCost =
        Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 0;
      payload.hotelAddress = form.hotelAddress ?? '';
    }
    return payload;
  };

  /** Refresca reserva + financials en el modal sin resetear el resto del formulario de edición. */
  const refreshBookingInEditModal = async (bookingId) => {
    const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}`));
    const data = await response.json();
    if (!data.success) return { success: false };
    const balanceDue = Number(data.financials?.balanceDue ?? 0);
    const paidTotal = Number(data.financials?.paidTotal ?? 0);
    const total = Number(data.data?.total ?? 0);
    const merged = { ...data.data, financials: data.financials };
    setSelectedBooking(merged);
    hydrateAccessoryDraftFromBooking(data.data);
    setEditForm((prev) => ({
      ...prev,
      deliveryType: data.data.deliveryType === 'HOTEL' ? 'DELIVERY' : data.data.deliveryType,
      deliveryCost:
        data.data.deliveryCost != null && data.data.deliveryCost !== ''
          ? String(data.data.deliveryCost)
          : '0',
      hotelAddress: data.data.hotelAddress || ''
    }));
    return { success: true, balanceDue, paidTotal, total, merged };
  };

  const openPostApplyFinancialIfNeeded = (before, after, bookingForPayments) => {
    if (!before || !after) return;
    const delta = roundMoney(after.balanceDue - before.balanceDue);
    const breakdown = computePriceDeltaBreakdown(before, after);
    const hasQuipu = bookingHasPaidInvoicedQuipu(bookingForPayments);
    const fiscalSplitRequired = hasQuipu && hasOppositePriceDeltas(breakdown);

    if (delta > 0.02) {
      const suggested = Math.min(delta, Math.max(0, after.balanceDue));
      setPostApplyFinancialModal({
        kind: 'supplement',
        oldDue: before.balanceDue,
        newDue: after.balanceDue,
        suggestedAmount: roundMoney(suggested),
        breakdown,
        fiscalSplitRequired,
        hasQuipuInvoicedPayments: hasQuipu,
      });
    } else if (delta < -0.02) {
      const creditHint = after.paidTotal > after.total + 0.02;
      setPostApplyFinancialModal({
        kind: 'due_down',
        oldDue: before.balanceDue,
        newDue: after.balanceDue,
        creditHint,
        breakdown,
        fiscalSplitRequired,
        hasQuipuInvoicedPayments: hasQuipu,
      });
    }
  };

  const postPendingSupplementQuipuInvoiceRequest = async (bookingId, body) => {
    const res = await authenticatedFetch(
      buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}/pending-supplement-quipu-invoice`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    );
    return res.json();
  };

  useEffect(() => {
    if (!showEditModal || !selectedBooking?.id || user?.role === 'AGENCY') {
      setPendingSupplementPreview(null);
      setPendingSupplementPreviewLoading(false);
      return;
    }
    // La vista previa de Quipu se calcula con el estado guardado en servidor.
    // Si hay cambios económicos sin aplicar (entrega o accesorios en borrador), ocultamos la preview para evitar
    // mostrar un “pendiente” que ya no corresponde a lo que el usuario está dejando en el formulario.
    const { hasUnsavedEconomicDraft } = getPendingSupplementDraftExtraLines(
      selectedBooking,
      editForm,
      accessoryDraftByRentalId
    );
    if (hasUnsavedEconomicDraft) {
      setPendingSupplementPreview(null);
      setPendingSupplementPreviewLoading(false);
      return;
    }
    const due = Number(selectedBooking.financials?.balanceDue ?? 0);
    if (due <= 0.02) {
      setPendingSupplementPreview(null);
      setPendingSupplementPreviewLoading(false);
      return;
    }
    const bookingId = selectedBooking.id;
    let previewUrl = buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${bookingId}/pending-supplement-preview`);
    if (showPartialSupplementAmount) {
      const raw = String(supplementQuipuAmount || '').trim().replace(',', '.');
      if (raw !== '') {
        const n = parseFloat(raw);
        if (Number.isFinite(n) && n > 0) {
          previewUrl += `?amountEur=${encodeURIComponent(String(n))}`;
        }
      }
    }
    let cancelled = false;
    setPendingSupplementPreviewLoading(true);
    (async () => {
      try {
        const res = await authenticatedFetch(previewUrl);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) {
          setPendingSupplementPreview(data);
        } else {
          setPendingSupplementPreview(null);
        }
      } catch {
        if (!cancelled) setPendingSupplementPreview(null);
      } finally {
        if (!cancelled) setPendingSupplementPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Dep. granular: incluye borrador de accesorios y entrega sin listar `selectedBooking`/`editForm` enteros (evita refetch en cada tecla).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ver comentario anterior
  }, [
    showEditModal,
    selectedBooking?.id,
    user?.role,
    selectedBooking?.total,
    selectedBooking?.vehiclesTotal,
    selectedBooking?.accessoriesTotal,
    selectedBooking?.insuranceTotal,
    selectedBooking?.deliveryCost,
    selectedBooking?.deliveryType,
    selectedBooking?.financials?.balanceDue,
    showPartialSupplementAmount,
    supplementQuipuAmount,
    authenticatedFetch,
    accessoryDraftByRentalId,
    editForm.deliveryType,
    editForm.deliveryCost
  ]);

  useEffect(() => {
    if (!showEditModal) return;
    setAccessoriesByModelEdit({});
  }, [showEditModal, selectedBooking?.id]);

  useEffect(() => {
    if (!showEditModal || !selectedBooking?.vehicleRentals) return;
    hydrateAccessoryDraftFromBooking(selectedBooking);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir / cambiar reserva
  }, [showEditModal, selectedBooking?.id]);

  useEffect(() => {
    if (!showEditModal || !selectedBooking?.vehicleRentals?.length) return;
    const modelIds = [...new Set(selectedBooking.vehicleRentals.map((vr) => vr.vehicle?.modelId).filter(Boolean))];
    let cancelled = false;
    (async () => {
      for (const mid of modelIds) {
        setAccessoriesByModelEdit((prev) => {
          if (Object.prototype.hasOwnProperty.call(prev, mid)) return prev;
          return { ...prev, [mid]: null };
        });
        if (cancelled) return;
        try {
          const r = await authenticatedFetch(`${buildApiUrl(API_ENDPOINTS.ACCESSORIES)}/model/${mid}?lang=es`);
          const j = await r.json();
          if (cancelled) return;
          setAccessoriesByModelEdit((p) => ({ ...p, [mid]: Array.isArray(j) ? j : [] }));
        } catch (e) {
          console.error(e);
          if (cancelled) return;
          setAccessoriesByModelEdit((p) => ({ ...p, [mid]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showEditModal, selectedBooking?.id, selectedBooking?.vehicleRentals, authenticatedFetch]);

  const toggleAccessoryDraft = (rentalId, accessory) => {
    if (user?.role === 'AGENCY' || !selectedBooking) return;
    const duration = computeDurationDays(selectedBooking.startDateTime, selectedBooking.endDateTime);
    const isDaily = accessory.accessoryPriceType === 'DAILY';
    const price = isDaily ? Number(accessory.price) * duration : Number(accessory.price);
    setAccessoryDraftByRentalId((prev) => {
      const cur = [...(prev[rentalId] || [])];
      const idx = cur.findIndex((x) => Number(x.accessoryId) === Number(accessory.id));
      if (idx >= 0) {
        cur.splice(idx, 1);
        queueMicrotask(() =>
          setEditAccessoryInlineErrorByRentalId((p) => {
            if (!p?.[rentalId]) return p;
            return { ...p, [rentalId]: null };
          })
        );
      }
      else {
        const isInsurance =
          accessory?.category === 'INSURANCE' || String(accessory?.category || '').toUpperCase() === 'INSURANCE';
        if (isInsurance) {
          const alreadyHasInsurance = cur.some((x) => {
            const cat = resolveDraftAccessoryCategory(rentalId, x.accessoryId);
            return cat === 'INSURANCE' || String(cat || '').toUpperCase() === 'INSURANCE';
          });
          if (alreadyHasInsurance) {
            queueMicrotask(() =>
              setEditAccessoryInlineErrorByRentalId((p) => ({
                ...(p || {}),
                [rentalId]:
                  'Solo puedes seleccionar un seguro por bicicleta. Desmarca el seguro actual para poder elegir otro.',
              }))
            );
            return prev;
          }
        }
        cur.push({ accessoryId: accessory.id, price });
        queueMicrotask(() =>
          setEditAccessoryInlineErrorByRentalId((p) => {
            if (!p?.[rentalId]) return p;
            return { ...p, [rentalId]: null };
          })
        );
      }
      const next = { ...prev, [rentalId]: cur };
      const removalBlock = removedServerAccessoryBlockMessage(selectedBooking, accessoryServerSnapshotByRentalId, next);
      if (removalBlock) {
        queueMicrotask(() =>
          setEditAccessoryInlineErrorByRentalId((p) => ({
            ...(p || {}),
            [removalBlock.rentalId || rentalId]: removalBlock.message || String(removalBlock),
          }))
        );
        // Permitir seguir editando en el modal (p. ej. quitar y volver a marcar / sustituir),
        // pero bloqueará al confirmar “Aplicar…” si el estado final sigue siendo una bajada no permitida.
        return next;
      }
      if (priceEditBlocked(selectedBooking, editForm, next)) {
        queueMicrotask(() => setError(priceEditBlockMessage(selectedBooking, editForm, next)));
        return prev;
      }
      return next;
    });
  };

  const executeSaveBikeLines = async () => {
    if (!selectedBooking?.id) {
      return { success: false, errorMessage: 'Sin reserva' };
    }
    const rentalUpdates = (selectedBooking.vehicleRentals || []).map((vr) => ({
      rentalId: vr.id,
      accessories: (accessoryDraftByRentalId[vr.id] || []).map((a) => ({
        accessoryId: a.accessoryId,
        price: a.price
      }))
    }));
    try {
      const res = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/bike-lines`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rentalUpdates })
      });
      const data = await res.json();
      if (data.success) {
        const merged = { ...data.data, financials: data.financials };
        setSelectedBooking(merged);
        hydrateAccessoryDraftFromBooking(data.data);
        // Mantener el snapshot base de la sesión de edición (al abrir el modal):
        // así se pueden revertir/ajustar accesorios en la misma sesión aunque ya se hayan aplicado cambios.
        fetchBookings();
        return {
          success: true,
          snapshot: financialSnapshotFromBooking(merged)
        };
      }
      return { success: false, errorMessage: data.message || 'Error al guardar líneas' };
    } catch (e) {
      console.error(e);
      return { success: false, errorMessage: 'Error al guardar líneas' };
    }
  };

  const executeApplyDelivery = async () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') {
      return { success: false, errorMessage: 'No se puede aplicar' };
    }
    const addrMsg = deliveryStreetRequiredMessage(editForm);
    if (addrMsg) {
      return { success: false, errorMessage: addrMsg };
    }
    try {
      const deliveryPayload = buildDeliveryPayloadFromEditForm(editForm);
      const res2 = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deliveryPayload)
      });
      const d2 = await res2.json();
      if (!d2.success) {
        return { success: false, errorMessage: d2.message || 'Error al guardar datos de entrega' };
      }
      const r = await refreshBookingInEditModal(selectedBooking.id);
      if (!r.success) {
        return { success: false, errorMessage: 'No se pudo refrescar la reserva' };
      }
      fetchBookings();
      return {
        success: true,
        snapshot: financialSnapshotFromBooking(r.merged),
        merged: r.merged,
      };
    } catch (e) {
      console.error(e);
      return { success: false, errorMessage: 'Error al aplicar entrega' };
    }
  };

  const handleConfirmApplyLinesAndDelivery = async () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') return;
    const blockApply = priceEditBlockMessage(selectedBooking, editForm, accessoryDraftByRentalId);
    if (blockApply) {
      setShowConfirmApplyLinesAndDelivery(false);
      setError(blockApply);
      return;
    }
    const removalBlock = removedServerAccessoryBlockMessage(
      selectedBooking,
      accessoryServerSnapshotByRentalId,
      accessoryDraftByRentalId
    );
    if (removalBlock) {
      setShowConfirmApplyLinesAndDelivery(false);
      setEditAccessoryInlineErrorByRentalId((p) => ({
        ...(p || {}),
        [removalBlock.rentalId]: removalBlock.message,
      }));
      return;
    }
    setShowConfirmApplyLinesAndDelivery(false);
    const before = financialSnapshotFromBooking(selectedBooking);
    setSavingApplyLinesAndDelivery(true);
    setError(null);
    try {
      const hasRentals = (selectedBooking.vehicleRentals || []).length > 0;
      if (hasRentals) {
        const r1 = await executeSaveBikeLines();
        if (!r1.success) {
          setError(r1.errorMessage || 'Error al guardar líneas');
          return;
        }
      }
      const r2 = await executeApplyDelivery();
      if (!r2.success) {
        setError(r2.errorMessage || 'Error al aplicar entrega');
        return;
      }
      openPostApplyFinancialIfNeeded(before, r2.snapshot, r2.merged);
    } finally {
      setSavingApplyLinesAndDelivery(false);
    }
  };

  const handleConfirmApplyLinesAndEmitQuipu = async () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') return;
    const blockEmit = priceEditBlockMessage(selectedBooking, editForm, accessoryDraftByRentalId);
    if (blockEmit) {
      setShowConfirmApplyLinesAndEmitQuipu(false);
      setError(blockEmit);
      return;
    }
    setShowConfirmApplyLinesAndEmitQuipu(false);
    setSavingApplyLinesAndEmitQuipu(true);
    setError(null);
    const beforeSnap = financialSnapshotFromBooking(selectedBooking);
    try {
      const hasRentals = (selectedBooking.vehicleRentals || []).length > 0;
      if (hasRentals) {
        const r1 = await executeSaveBikeLines();
        if (!r1.success) {
          setError(r1.errorMessage || 'Error al guardar líneas');
          return;
        }
      }
      const r2 = await executeApplyDelivery();
      if (!r2.success) {
        setError(r2.errorMessage || 'Error al aplicar entrega');
        return;
      }
      const breakdown = computePriceDeltaBreakdown(beforeSnap, r2.snapshot);
      const fiscalSplitAuto =
        bookingHasPaidInvoicedQuipu(r2.merged) && hasOppositePriceDeltas(breakdown);
      if (fiscalSplitAuto) {
        openPostApplyFinancialIfNeeded(beforeSnap, r2.snapshot, r2.merged);
        setError(
          'Hay facturas en Quipu previas y este cambio mezcla bajadas y subidas. No se emite una factura automática única: sigue las instrucciones del aviso o del modal (rectificativa + suplemento por separado).'
        );
        fetchBookings();
        return;
      }
      const dueAfter = Number(r2.snapshot?.balanceDue ?? 0);
      if (dueAfter <= 0.02) {
        // Sin saldo pendiente no hay factura suplementaria en Quipu (p. ej. cancelación o total cubierto).
        // No es un fallo: mismo criterio que «Aplicar cambios» — aviso financiero si hay abono/saldo a favor.
        setError(null);
        openPostApplyFinancialIfNeeded(beforeSnap, r2.snapshot, r2.merged);
        await refreshBookingInEditModal(selectedBooking.id);
        fetchBookings();
        return;
      }
      const data = await postPendingSupplementQuipuInvoiceRequest(selectedBooking.id, {});
      if (!data.success) {
        setError(data.message || 'No se pudo emitir la factura en Quipu');
        await refreshBookingInEditModal(selectedBooking.id);
        fetchBookings();
        return;
      }
      await refreshBookingInEditModal(selectedBooking.id);
      setSupplementQuipuAmount('');
      fetchBookings();
    } catch (e) {
      setError('Error al aplicar cambios o al emitir la factura');
      console.error(e);
    } finally {
      setSavingApplyLinesAndEmitQuipu(false);
    }
  };

  const handleCreditAbonoQuipu = async () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') return;
    try {
      setCreditAbonoLoading(true);
      setError(null);
      const res = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/credit-abono-quipu`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refundPaymentMethod: creditAbonoPaymentMethod })
        }
      );
      const data = await res.json();
      if (data.success) {
        const refresh = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}`));
        const refreshed = await refresh.json();
        if (refreshed.success) {
          setSelectedBooking({ ...refreshed.data, financials: refreshed.financials });
          hydrateAccessoryDraftFromBooking(refreshed.data);
        }
        fetchBookings();
      } else {
        setError(data.message || 'No se pudo emitir el abono en Quipu');
      }
    } catch (e) {
      setError('Error al emitir el abono en Quipu');
      console.error(e);
    } finally {
      setCreditAbonoLoading(false);
    }
  };

  /** Categoría de accesorio en borrador: reserva guardada o catálogo cargado por modelo (edición). */
  const resolveDraftAccessoryCategory = (rentalId, accessoryId) => {
    const vr = selectedBooking?.vehicleRentals?.find((r) => r.id === rentalId);
    if (!vr) return undefined;
    const saved = (vr.accessories || []).find(
      (a) => Number(a.accessoryId) === Number(accessoryId)
    );
    if (saved?.accessory?.category) return saved.accessory.category;
    const mid = vr?.vehicle?.modelId;
    const list = mid != null ? accessoriesByModelEdit[mid] : null;
    const acc = Array.isArray(list) ? list.find((x) => Number(x.id) === Number(accessoryId)) : null;
    return acc?.category;
  };

  /** Importe a facturar / cobrar (coherente con vista previa Quipu y siguiente cobro esperado). */
  const resolvePendingSupplementPayAmountEur = () => {
    const fin = selectedBooking?.financials;
    if (!fin) return { error: 'No hay detalle financiero.' };
    const serverDue = Number(fin.balanceDue ?? 0);
    const nextPay = Number(fin.nextExpectedPaymentAmount ?? 0);
    const draftExtra = getPendingSupplementDraftExtraLines(
      selectedBooking,
      editForm,
      accessoryDraftByRentalId
    );
    if (draftExtra.hasUnsavedEconomicDraft) {
      return {
        error:
          'Tienes cambios de accesorios o entrega sin aplicar. Pulsa «Aplicar accesorios y entrega» (o «Aplicar y emitir factura Quipu») para guardarlos antes de registrar el cobro: el importe a cobrar debe coincidir con el total ya guardado en el servidor.',
      };
    }
    const effectiveDueCap = nextPay > 0 ? nextPay : serverDue;
    let amount = effectiveDueCap;
    if (showPartialSupplementAmount) {
      const raw = String(supplementQuipuAmount || '').trim().replace(',', '.');
      if (raw === '') {
        return {
          error:
            'Indica un importe o desactiva «Facturar solo parte del pendiente» para usar el total automático.',
        };
      }
      const n = parseFloat(raw);
      if (!Number.isFinite(n) || n <= 0) {
        return { error: 'Introduce un importe válido.' };
      }
      amount = Math.round(n * 100) / 100;
    }
    if (!Number.isFinite(amount) || amount <= 0.02) {
      return { error: 'No hay importe pendiente válido.' };
    }
    if (amount > effectiveDueCap + 0.02) {
      return {
        error: `El importe no puede superar el saldo pendiente (${effectiveDueCap.toFixed(2)} €).`,
      };
    }
    return { amountEur: amount };
  };

  const openConfirmManualPayWithQuipu = () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') return;
    setError(null);
    const fin = selectedBooking.financials;
    const paid = Number(fin?.paidTotal ?? 0);
    const serverDue = Number(fin?.balanceDue ?? 0);
    const draftExtra = getPendingSupplementDraftExtraLines(
      selectedBooking,
      editForm,
      accessoryDraftByRentalId
    );
    if (draftExtra.hasUnsavedEconomicDraft) {
      const t = computeCobrarSaldoPendingDraftTable(
        selectedBooking,
        editForm,
        accessoryDraftByRentalId,
        paid,
        resolveDraftAccessoryCategory
      );
      if (t.pendienteEstimado <= 0.02) {
        setError('No hay saldo pendiente estimado con el borrador actual.');
        return;
      }
    } else {
      if (serverDue <= 0.02) {
        setError('No hay saldo pendiente para registrar.');
        return;
      }
    }
    const r = resolvePendingSupplementPayAmountEur();
    if (r.error) {
      setError(r.error);
      return;
    }
    setShowConfirmManualPayWithQuipuModal(true);
  };

  const handleConfirmManualPayWithQuipu = async () => {
    if (!selectedBooking?.id || user?.role === 'AGENCY') return;
    const r = resolvePendingSupplementPayAmountEur();
    if (r.error) {
      setError(r.error);
      setShowConfirmManualPayWithQuipuModal(false);
      return;
    }
    const { amountEur } = r;
    try {
      setConfirmManualPayLoading(true);
      setError(null);

      const issueData = await postPendingSupplementQuipuInvoiceRequest(selectedBooking.id, {
        amountEur,
      });

      const dupPending =
        !issueData.success &&
        typeof issueData.message === 'string' &&
        /ya hay una factura/i.test(issueData.message);

      if (!issueData.success && !dupPending) {
        setError(issueData.message || 'No se pudo crear la factura en Quipu');
        return;
      }

      const offlineRes = await authenticatedFetch(
        buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}/balance-payment-offline`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amountEur, paymentMethod: offlinePayMethod }),
        }
      );
      const offlineData = await offlineRes.json();
      if (!offlineData.success) {
        setError(
          offlineData.message ||
            'No se pudo registrar el cobro. Si la factura en Quipu ya se había creado, revisa el importe y vuelve a intentarlo.'
        );
        await refreshBookingInEditModal(selectedBooking.id);
        fetchBookings();
        return;
      }

      setShowConfirmManualPayWithQuipuModal(false);
      await handleEditBooking({ id: selectedBooking.id });
      setSupplementQuipuAmount('');
      fetchBookings();
    } catch (e) {
      setError('Error al registrar el cobro o la factura en Quipu');
      console.error(e);
    } finally {
      setConfirmManualPayLoading(false);
    }
  };

  // Guardar cambios (llamado tras confirmar en el modal)
  const handleSaveBooking = async () => {
    if (user?.role === 'AGENCY') {
      setError('No tienes permisos para editar reservas');
      return;
    }

    try {
      setSaving(true);
      setShowConfirmSaveModal(false);
      const isPast = selectedBooking.endDateTime && new Date(selectedBooking.endDateTime) < new Date();
      const { startDateTime: _sd, endDateTime: _ed, ...editFormNoDates } = editForm;
      const payload = isPast ? { notes: editForm.notes ?? '' } : { ...editFormNoDates };
      if (!isPast && payload.refundStatus === '') delete payload.refundStatus;
      if (!isPast) {
        if (payload.deliveryType === 'PICKUP') {
          payload.deliveryCost = 0;
        } else if (payload.deliveryCost !== undefined) {
          const n = parseFloat(String(payload.deliveryCost).replace(',', '.'));
          payload.deliveryCost =
            Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : 0;
        }
      }
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/${selectedBooking.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        closeEditModal();
        fetchBookings();
        fetchStats();
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Error al actualizar la reserva');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de confirmación antes de guardar
  const handleRequestSaveBooking = () => {
    setShowConfirmSaveModal(true);
  };

  // Manejar creación exitosa de reserva
  const handleBookingCreated = (newBooking) => {
    setShowCreateModal(false);
    setCreateReservaCustomerPrefill(null);
    // Recargar la lista de reservas
    fetchBookings();
    fetchStats();
    // Mostrar mensaje de éxito (opcional)
    console.log('Reserva creada exitosamente:', newBooking);
  };

  const openCreateReservationModal = () => {
    setCreateReservaCustomerPrefill(null);
    setCreateReservaModalKey((k) => k + 1);
    setShowCreateModal(true);
  };

  const openCreateReservationFromViewedBooking = () => {
    if (!selectedBooking) return;
    const prefill = buildCustomerPrefillFromBooking(selectedBooking);
    setCreateReservaCustomerPrefill(prefill);
    setCreateReservaModalKey((k) => k + 1);
    setShowViewModal(false);
    setShowCreateModal(true);
  };

  // Reenviar correo de confirmación
  const handleResendEmail = async (bookingId) => {
    try {
      setResendingEmail(true);
      setError(null);
      
      const response = await authenticatedFetch(buildApiUrl(`/api/email/resend/${bookingId}`), {
        method: 'POST'
      });

      const data = await response.json();
      
      if (data.success) {
        // Actualizar el estado de la reserva en el modal
        setSelectedBooking(prev => ({
          ...prev,
          emailSent: true
        }));
        
        // Recargar la lista de reservas para actualizar el estado
        fetchBookings();
        
        // Mostrar mensaje de éxito (opcional, puedes usar un toast o similar)
        console.log('Email reenviado correctamente');
      } else {
        setError(data.message || 'Error al reenviar el correo');
      }
    } catch (error) {
      console.error('Error al reenviar correo:', error);
      setError('Error al reenviar el correo de confirmación');
    } finally {
      setResendingEmail(false);
    }
  };

  // Formatear fecha y hora en zona horaria de Madrid
  // Convierte fechas UTC de la base de datos a hora local de Madrid
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Fecha inválida';
    }
  };

  // Formatear solo fecha en zona horaria de Madrid
  // Convierte fechas UTC de la base de datos a fecha local de Madrid
  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Fecha inválida';
    }
  };

  // Formatear estado
  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': { label: 'Pendiente', class: 'pending' },
      'CONFIRMED': { label: 'Confirmada', class: 'confirmed' },
      'CANCELLED': { label: 'Cancelada', class: 'cancelled' },
      'COMPLETED': { label: 'Completada', class: 'completed' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'unknown' };
    
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getRefundStatusBadge = (refundStatus) => {
    if (!refundStatus) return null;
    const map = {
      'PENDING_REFUND': { label: 'Pendiente de abono', class: 'pending' },
      'REFUNDED': { label: 'Abonada', class: 'completed' }
    };
    const info = map[refundStatus] || { label: refundStatus, class: 'unknown' };
    return (
      <span className={`status-badge refund-status ${info.class}`}>
        {info.label}
      </span>
    );
  };

  // Pago principal de la reserva (ledger: una reserva puede tener varios movimientos)
  const getMainPayment = (booking) => {
    const payments = booking?.payments;
    if (!payments?.length) return null;
    return payments.find((p) => p.status === 'PAID') || payments[0];
  };

  /** Ticket colaborador guardado en Payment.agencyReference (cualquier movimiento de la reserva) */
  const getCollaboratorTicket = (booking) => {
    const payments = booking?.payments;
    if (!payments?.length) return null;
    const p = payments.find((x) => x.agencyReference && String(x.agencyReference).trim());
    return p ? String(p.agencyReference).trim() : null;
  };

  // Formatear método de pago (soporta payment.method legacy y payment.paymentMethod nuevo)
  const getPaymentMethodBadge = (payment) => {
    const method = payment?.paymentMethod ?? payment?.method;
    if (!payment || !method) {
      return (
        <span className="payment-badge unknown">
          N/A
        </span>
      );
    }

    const methodMap = {
      'CASH': { label: 'Efectivo', class: 'cash' },
      'CARD': { label: 'Tarjeta', class: 'card' },
      'STRIPE': { label: 'Tarjeta', class: 'card' },
      'TPV': { label: 'TPV', class: 'card' },
      'COLABORADOR': { label: 'Colaborador', class: 'colaborador' },
      'AGENCY': { label: 'Colaborador', class: 'colaborador' },
      'TRANSFER': { label: 'Transferencia', class: 'transfer' },
      'BANK_TRANSFER': { label: 'Transferencia', class: 'transfer' },
      'BIZUM': { label: 'Bizum', class: 'bizum' },
      'NONE': { label: '—', class: 'unknown' }
    };

    const methodInfo = methodMap[method] || { label: method, class: 'unknown' };

    return (
      <span className={`payment-badge ${methodInfo.class}`}>
        {methodInfo.label}
      </span>
    );
  };

  // Etiqueta del tipo de negocio de la reserva
  const getBusinessGroupLabel = (businessGroup) => {
    const map = {
      'BIKES': 'Bikes',
      'JET_SKI': 'Jet Ski',
      'PARASAILING': 'Parasailing',
      'BUGGY': 'Buggy'
    };
    return map[businessGroup] || businessGroup || 'Bikes';
  };

  // Formatear estado del contrato
  const getContractBadge = (contracts) => {
    if (!contracts || contracts.length === 0) {
      return (
        <span className="contract-badge none">
          Sin contrato
        </span>
      );
    }

    // Verificar si hay algún contrato firmado
    const hasSignedContract = contracts.some(contract => contract.status === 'SIGNED');
    const hasDraftContract = contracts.some(contract => contract.status === 'DRAFT');
    
    if (hasSignedContract) {
      return (
        <span className="contract-badge signed">
          Firmado
        </span>
      );
    }
    
    if (hasDraftContract) {
      return (
        <span className="contract-badge draft">
          Borrador
        </span>
      );
    }

    return (
      <span className="contract-badge none">
        Sin contrato
      </span>
    );
  };

  /** Columna listado: saldo pendiente, pagado y abono / saldo a favor pendiente de gestionar */
  const getBookingPaymentColumn = (booking) => {
    const fin = booking.financials;
    const total = Number(booking.total) || 0;
    const paid = fin != null && fin.paidTotal != null ? Number(fin.paidTotal) : NaN;
    const creditEur =
      Number.isFinite(paid) && paid > total + 0.02 ? Math.round((paid - total) * 100) / 100 : 0;

    if (booking.status === 'CANCELLED') {
      if (booking.refundStatus === 'PENDING_REFUND') {
        const amt =
          creditEur > 0.02
            ? ` €${creditEur.toFixed(2)}`
            : '';
        return (
          <span
            className="payment-col-badge pending-refund"
            title={
              Number.isFinite(paid)
                ? `Cancelada · Abono al cliente pendiente · Cobrado: €${paid.toFixed(2)} · Total reserva: €${total.toFixed(2)}`
                : 'Cancelada · Abono al cliente pendiente'
            }
          >
            Abono pendiente{amt}
          </span>
        );
      }
      if (booking.refundStatus === 'REFUNDED') {
        return (
          <span className="payment-col-badge ok" title="Cancelada · Abono registrado">
            Abonado
          </span>
        );
      }
      return <span className="payment-col-badge muted">—</span>;
    }

    if (!fin || fin.balanceDue == null) {
      return (
        <span className="payment-col-badge muted" title="Sin datos de cobro en listado">
          N/D
        </span>
      );
    }
    const bal = Number(fin.balanceDue);
    const paidOk = Number(fin.paidTotal);
    const hasPendingPay = bal > 0.02;
    const hasCredit = creditEur > 0.02;

    if (hasPendingPay || hasCredit) {
      return (
        <span className="payment-col-stack">
          {hasPendingPay && (
            <span
              className="payment-col-badge pending-balance"
              title={`Cobrado: €${paidOk.toFixed(2)} · Pendiente de cobro: €${bal.toFixed(2)}${
                booking.paymentMode === 'PARTIAL' ? ' · Modo: pago parcial' : ''
              }`}
            >
              Pendiente €{bal.toFixed(2)}
            </span>
          )}
          {hasCredit && (
            <span
              className="payment-col-badge pending-refund"
              title={`Saldo a favor del cliente: €${creditEur.toFixed(2)} (cobrado €${paidOk.toFixed(2)} − total €${total.toFixed(2)}). Gestionar abono en edición · Cobros.`}
            >
              Abono pendiente €{creditEur.toFixed(2)}
            </span>
          )}
        </span>
      );
    }
    return (
      <span className="payment-col-badge ok" title={`Cobrado: €${paidOk.toFixed(2)}`}>
        Pagado
      </span>
    );
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="loading-container">
        <Loader className="loading-spinner" />
        <p>Cargando reservas...</p>
      </div>
    );
  }

  // Los días actuales de la reserva siguen siendo elegibles aunque estén cerrados (el backend lo permite)
  const scheduleOriginalDays = new Set(
    [selectedBooking?.startDateTime, selectedBooking?.endDateTime]
      .filter(Boolean)
      .map((iso) => isoToDatetimeLocalValue(iso).slice(0, 10))
  );
  const scheduleClosedDays = closedDays.filter((d) => !scheduleOriginalDays.has(d));

  return (
    <div className="reservas-page">
      <div className="page-header">
        <h1>Gestión de Reservas</h1>
        <div className="header-actions">
          <button className="btn-primary" onClick={openCreateReservationModal}>
            <Plus size={16} />
            Nueva Reserva
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Reservas</h3>
          <p className="stat-number">{pagination.total}</p>
        </div>
        <div className="stat-card">
          <h3>Pendientes</h3>
          <p className="stat-number">{stats.PENDING || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Confirmadas</h3>
          <p className="stat-number">{stats.CONFIRMED || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Completadas</h3>
          <p className="stat-number">{stats.COMPLETED || 0}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        {customerFilter && (
          <div className="customer-filter-banner">
            <User size={16} />
            <span>
              Reservas del cliente <strong>{customerFilter.fullName}</strong>
              {customerFilter.email && <> ({customerFilter.email})</>}
            </span>
            <button
              type="button"
              className="btn-icon"
              title="Quitar filtro de cliente"
              onClick={clearCustomerFilter}
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div className="filters-grid">
          <div className="filter-group">
            <label>Estado</label>
            <select 
            className='text-black'
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="PENDING">Pendiente</option>
              <option value="CONFIRMED">Confirmada</option>
              <option value="CANCELLED">Cancelada</option>
              <option value="COMPLETED">Completada</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Método de Pago</label>
            <select 
              className='text-black'
              value={filters.paymentMethod} 
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="CASH">Efectivo</option>
              <option value="CARD">Tarjeta</option>
              <option value="TRANSFER">Transferencia</option>
              <option value="BIZUM">Bizum</option>
              <option value="COLABORADOR">Colaborador</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Origen</label>
            <select
              className="text-black"
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="fareharbor">FareHarbor</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Cobros y abonos</label>
            <select
              className="text-black"
              value={filters.financialFilter}
              onChange={(e) => handleFilterChange('financialFilter', e.target.value)}
              title="Cobro del importe de la reserva o abono al cliente en cancelaciones"
            >
              <option value="">Todos</option>
              <optgroup label="Importe de la reserva">
                <option value="SETTLED">Sin pendiente (liquidado)</option>
                <option value="OUTSTANDING">Con saldo pendiente</option>
              </optgroup>
              <optgroup label="Abono al cliente (canceladas)">
                <option value="PENDING_REFUND">Pendiente de abono</option>
                <option value="REFUNDED">Abonada</option>
              </optgroup>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Cliente</label>
            <input
              type="text"
              placeholder="Email o nombre…"
              value={filters.customerSearch}
              onChange={(e) => handleFilterChange('customerSearch', e.target.value)}
              autoComplete="off"
            />
          </div>
          
          <DateRangeFilter
            idPrefix="booking-created"
            label="Fecha de creación"
            from={filters.createdFrom}
            to={filters.createdTo}
            onRangeChange={handleCreatedRangeChange}
            dialogAriaLabel="Rango de fechas de creación de la reserva"
          />

          <DateRangeFilter
            idPrefix="booking-rental"
            label="Fechas de reserva"
            from={filters.startDate}
            to={filters.endDate}
            onRangeChange={handleRentalRangeChange}
            dialogAriaLabel="Rango de fechas del alquiler"
          />
          
          <div className="filter-actions">
            <button className="btn-secondary" onClick={clearFilters}>
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-container reservas-table-container">
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {(tableScroll.canLeft || tableScroll.canRight) && (
          <div className="table-scroll-controls">
            <button
              className="btn-icon"
              title="Desplazar a la izquierda"
              onClick={() => scrollTable(-1)}
              disabled={!tableScroll.canLeft}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="btn-icon"
              title="Desplazar a la derecha"
              onClick={() => scrollTable(1)}
              disabled={!tableScroll.canRight}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        <div className="reservas-table-scroll" ref={tableScrollRef} onScroll={updateTableScroll}>
        <table className="data-table reservas-table">
          <thead>
            <tr>
              <th>ID</th>
              {SHOW_BUSINESS_GROUP_COLUMN && <th>Tipo</th>}
              <th>Cliente</th>
              <th>Vehículos</th>
              <th>Fechas</th>
              <th>Total</th>
              <th>Método de Pago</th>
              <th>Estado</th>
              <th>Cobro</th>
              <th>Contrato</th>
              <th>Creada</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                    <span>#{booking.id}</span>
                    {booking.externalSource === 'FAREHARBOR' && (
                      <span className="fareharbor-source-badge" title={booking.bookingReference || booking.externalId || ''}>
                        FareHarbor
                      </span>
                    )}
                  </div>
                </td>
                {SHOW_BUSINESS_GROUP_COLUMN && (
                  <td>
                    <span className="business-group-badge">{getBusinessGroupLabel(booking.businessGroup)}</span>
                  </td>
                )}
                <td>
                  <div className="customer-info">
                    <strong>{booking.customer?.fullName || booking.guestName || 'N/A'}</strong>
                    <small>{booking.customer?.email || booking.guestEmail || 'N/A'}</small>
                  </div>
                </td>
                <td>
                  <div className="vehicles-info">
                    {booking.vehicleRentals?.length > 0 ? (
                      booking.vehicleRentals.map((rental, index) => (
                        <div key={index} className="vehicle-item">
                          {rental.vehicle?.model?.name} ({rental.vehicle?.size})
                          {rental.vehicle?.serialNumber && (
                            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                              - {rental.vehicle.serialNumber}
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {booking.externalSource === 'FAREHARBOR'
                          ? (booking.activityMetadata?.fareharbor?.itemName || 'Sin vehículos (externo)')
                          : '—'}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="dates-info">
                    <div>Desde: {formatDateOnly(booking.startDateTime)}</div>
                    <div>Hasta: {formatDateOnly(booking.endDateTime)}</div>
                  </div>
                </td>
                <td>€{booking.total}</td>
                <td>{getPaymentMethodBadge(getMainPayment(booking))}</td>
                <td>
                  {getStatusBadge(booking.status)}
                  {booking.status === 'CANCELLED' && getRefundStatusBadge(booking.refundStatus) && (
                    <span className="refund-badge-wrap"> {getRefundStatusBadge(booking.refundStatus)}</span>
                  )}
                </td>
                <td>{getBookingPaymentColumn(booking)}</td>
                <td>{getContractBadge(booking.contracts)}</td>
                <td>
                  <div className="created-date">
                    <div className="date">{formatDateOnly(booking.createdAt)}</div>
                    <div className="time">{formatDateTime(booking.createdAt).split(' ')[1]}</div>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon" 
                      title="Ver detalles"
                      onClick={() => handleViewBooking(booking.id)}
                    >
                      <Eye size={16} />
                    </button>
                    {user?.role !== 'AGENCY' && (
                      <>
                        <button 
                          className="btn-icon" 
                          title="Editar"
                          onClick={() => handleEditBooking(booking)}
                        >
                          <Edit size={16} />
                        </button>
                      </>
                    )}
                    {user?.role === 'ADMIN' && (
                      <button
                        className="btn-icon"
                        title="Eliminar"
                        onClick={() => openDeleteBookingModal(booking.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Paginación */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="btn-secondary"
            >
              Anterior
            </button>
            
            <span className="page-info">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            
            <button 
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="btn-secondary"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Modal de Vista */}
      {showViewModal && selectedBooking && (
        <div className="modal-overlay">
          <div className="modal modal-booking-wide">
            <div className="modal-header">
              <h2>Detalles de la Reserva #{selectedBooking.id}</h2>
              <button onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="booking-details">
                <div className="detail-section">
                  <h3>Información del Cliente</h3>
                  <p><strong>Nombre:</strong> {selectedBooking.customer?.fullName || selectedBooking.guestName || 'N/A'}</p>
                  <p><strong>Email:</strong> {selectedBooking.customer?.email || selectedBooking.guestEmail || 'N/A'}</p>
                  <p><strong>Teléfono:</strong> {selectedBooking.customer?.phone || (selectedBooking.guestPhonePrefix && selectedBooking.guestPhone ? `${selectedBooking.guestPhonePrefix} ${selectedBooking.guestPhone}` : selectedBooking.guestPhone) || 'N/A'}</p>
                  {selectedBooking.customer?.dni || selectedBooking.guestDni ? (
                    <p><strong>DNI:</strong> {selectedBooking.customer?.dni || selectedBooking.guestDni}</p>
                  ) : null}
                </div>
                
                <div className="detail-section">
                  <h3>Detalles de la Reserva</h3>
                  <p><strong>Estado:</strong> {getStatusBadge(selectedBooking.status)}
                  {selectedBooking.status === 'CANCELLED' && getRefundStatusBadge(selectedBooking.refundStatus) && (
                    <> {getRefundStatusBadge(selectedBooking.refundStatus)}</>
                  )}</p>
                  <p><strong>Fecha Inicio:</strong> {formatDateTime(selectedBooking.startDateTime)}</p>
                  <p><strong>Fecha Fin:</strong> {formatDateTime(selectedBooking.endDateTime)}</p>
                  <p><strong>Total:</strong> €{selectedBooking.total}</p>
                  {selectedBooking.paymentMode && (
                    <p><strong>Modo de pago:</strong> {selectedBooking.paymentMode === 'PARTIAL' ? 'Pago parcial (anticipo + liquidación)' : 'Pago total'}</p>
                  )}
                  {selectedBooking.financials && (
                    <div style={{ marginTop: '8px', padding: '10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <p style={{ margin: '0 0 6px 0', fontWeight: 600 }}>Cobros y saldo</p>
                      <p style={{ margin: '4px 0' }}><strong>Cobrado:</strong> €{Number(selectedBooking.financials.paidTotal).toFixed(2)}</p>
                      <p style={{ margin: '4px 0' }}><strong>Pendiente:</strong> €{Number(selectedBooking.financials.balanceDue).toFixed(2)}</p>
                      {selectedBooking.financials.depositPercentApplied != null && (
                        <p style={{ margin: '4px 0', fontSize: '13px', color: '#166534' }}>
                          Anticipo configurado: {selectedBooking.financials.depositPercentApplied}% del total (importe en € enteros)
                        </p>
                      )}
                    </div>
                  )}
                  <p>
                    <strong>Tipo de Entrega:</strong>{' '}
                    {selectedBooking.deliveryType === 'DELIVERY' || selectedBooking.deliveryType === 'HOTEL'
                      ? 'Entrega a domicilio'
                      : 'Recogida en tienda'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <p style={{ margin: 0 }}>
                      <strong>Correo de Confirmación:</strong> 
                      {selectedBooking.emailSent ? (
                        <span style={{ color: '#10b981', marginLeft: '8px' }}>✓ Enviado</span>
                      ) : (
                        <span style={{ color: '#ef4444', marginLeft: '8px' }}>✗ No enviado</span>
                      )}
                    </p>
                    <button
                      onClick={() => handleResendEmail(selectedBooking.id)}
                      disabled={resendingEmail || selectedBooking.status === 'CANCELLED' || selectedBooking.status === 'COMPLETED' || selectedBooking.status === 'PENDING'}
                      title={['CANCELLED', 'COMPLETED', 'PENDING'].includes(selectedBooking.status) ? 'No se puede reenviar el correo en reservas pendientes, canceladas o completadas' : undefined}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: (resendingEmail || selectedBooking.status === 'CANCELLED' || selectedBooking.status === 'COMPLETED' || selectedBooking.status === 'PENDING') ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (resendingEmail || selectedBooking.status === 'CANCELLED' || selectedBooking.status === 'COMPLETED' || selectedBooking.status === 'PENDING') ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        opacity: (resendingEmail || selectedBooking.status === 'CANCELLED' || selectedBooking.status === 'COMPLETED' || selectedBooking.status === 'PENDING') ? 0.6 : 1,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!resendingEmail && selectedBooking.status !== 'CANCELLED' && selectedBooking.status !== 'COMPLETED' && selectedBooking.status !== 'PENDING') {
                          e.target.style.backgroundColor = '#2563eb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!resendingEmail && selectedBooking.status !== 'CANCELLED' && selectedBooking.status !== 'COMPLETED' && selectedBooking.status !== 'PENDING') {
                          e.target.style.backgroundColor = '#3b82f6';
                        }
                      }}
                    >
                      {resendingEmail ? (
                        <>
                          <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Mail size={14} />
                          Reenviar correo
                        </>
                      )}
                    </button>
                  </div>
                  {(() => {
                    const mainPayment = getMainPayment(selectedBooking);
                    const collaboratorTicket = getCollaboratorTicket(selectedBooking);
                    return mainPayment ? (
                      <>
                        <p>
                          <strong>Método de Pago:</strong> {getPaymentMethodBadge(mainPayment)}
                          {(mainPayment.paymentMethod === 'AGENCY' || mainPayment.method === 'COLABORADOR') && mainPayment.agency && (
                            <span style={{ marginLeft: '8px', fontSize: '13px', color: '#6b7280' }}>
                              ({mainPayment.agency.name} - {mainPayment.agency.code})
                            </span>
                          )}
                        </p>
                        {collaboratorTicket ? (
                          <p>
                            <strong>Ticket:</strong>{' '}
                            <span style={{ wordBreak: 'break-word' }}>{collaboratorTicket}</span>
                          </p>
                        ) : null}
                      </>
                    ) : null;
                  })()}
                  {selectedBooking.payments && selectedBooking.payments.length > 1 && (
                    <div style={{ marginTop: '12px' }}>
                      <p><strong>Movimientos de pago ({selectedBooking.payments.length})</strong></p>
                      <ul style={{ margin: '8px 0 0 16px', fontSize: '13px' }}>
                        {selectedBooking.payments.map((p) => (
                          <li key={p.id}>
                            #{p.id} — {p.paymentMethod || p.method || '—'} — €{Number(p.grossAmount ?? p.amount).toFixed(2)}
                            {p.partial ? ' (anticipo)' : ''}
                            {p.quipuInvoiceNumber ? ` — Fact. ${p.quipuInvoiceNumber}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedBooking.ledgerEntries && selectedBooking.ledgerEntries.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <p><strong>Libro de movimientos / trazabilidad</strong></p>
                      <div style={{ maxHeight: '220px', overflowY: 'auto', fontSize: '12px', marginTop: '8px' }}>
                        <table className="ledger-mini-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                              <th style={{ padding: '4px' }}>Fecha</th>
                              <th style={{ padding: '4px' }}>Evento</th>
                              <th style={{ padding: '4px' }}>Detalle</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedBooking.ledgerEntries.map((row) => (
                              <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</td>
                                <td style={{ padding: '6px 4px' }}>{row.eventType}</td>
                                <td style={{ padding: '6px 4px', wordBreak: 'break-word' }}>
                                  {row.paymentId ? `Pago #${row.paymentId}` : ''}
                                  {row.payload ? ` ${typeof row.payload === 'object' ? JSON.stringify(row.payload) : row.payload}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {(selectedBooking.deliveryType === 'DELIVERY' || selectedBooking.deliveryType === 'HOTEL') && selectedBooking.hotelAddress && (
                    <div style={{ marginTop: '8px' }}>
                      <p><strong>Dirección de Entrega:</strong></p>
                      <p style={{ marginLeft: '16px', color: '#6b7280' }}>{selectedBooking.hotelAddress}</p>
                    </div>
                  )}
                  <p><strong>Creada:</strong> {formatDateTime(selectedBooking.createdAt)}</p>
                  {selectedBooking.notes && selectedBooking.notes.trim() !== '' && (
                    <div style={{ marginTop: '12px' }}>
                      <p><strong>Notas de la reserva:</strong></p>
                      <p
                        style={{
                          marginLeft: '12px',
                          color: '#4b5563',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          backgroundColor: '#f9fafb',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        {selectedBooking.notes}
                      </p>
                    </div>
                  )}
                </div>
                
                {(() => {
                  const rentals = selectedBooking.vehicleRentals || [];
                  const { poolVehicles } = reconcileVehicleLinePrices(
                    selectedBooking,
                    rentals
                  );
                  const sumLineSubtotals =
                    Math.round(
                      rentals.reduce((s, r) => s + (Number(r.subtotal) || 0), 0) * 100
                    ) / 100;
                  const storedRaw = selectedBooking.vehiclesTotal;
                  const storedVtNum =
                    storedRaw != null && storedRaw !== '' ? Number(storedRaw) : NaN;
                  // Preferir suma de subtotales por línea (coincide con «Precio vehículo»); el agregado Booking.vehiclesTotal
                  // a veces queda desincronizado (p. ej. 100 en cabecera vs 120 en líneas).
                  const vehiclesSubtotalDisplay =
                    sumLineSubtotals > 0
                      ? sumLineSubtotals
                      : Number.isFinite(storedVtNum) && !Number.isNaN(storedVtNum)
                        ? Math.round(storedVtNum * 100) / 100
                        : poolVehicles;
                  return (
                    <>
                <div className="detail-section">
                  <h3>Vehículos y Accesorios</h3>
                  {selectedBooking.vehicleRentals?.map((rental, index) => {
                    const rentalAccessories = rental.accessories || [];
                    const displayBike =
                      Math.round((parseFloat(rental.subtotal) || 0) * 100) / 100;
                    const rentalAccessoriesTotal = rentalAccessories.reduce((sum, acc) => {
                      return sum + (parseFloat(acc.finalPrice) || 0);
                    }, 0);
                    const rentalTotal = displayBike + rentalAccessoriesTotal;
                    
                    return (
                      <div key={index} className="vehicle-detail">
                        <p><strong>Modelo:</strong> {rental.vehicle?.model?.name}</p>
                        <p><strong>Talla:</strong> {rental.vehicle?.size}</p>
                        {rental.inseamCm != null && Number.isFinite(Number(rental.inseamCm)) && (
                          <p><strong>Entrepierna:</strong> {Number(rental.inseamCm)} cm</p>
                        )}
                        {rental.vehicle?.serialNumber && (
                          <p><strong>Número de Serie:</strong> {rental.vehicle.serialNumber}</p>
                        )}
                        {rental.vehicle?.id && (
                          <p><strong>ID Vehículo:</strong> #{rental.vehicle.id}</p>
                        )}
                        <p><strong>Precio vehículo:</strong> €{displayBike.toFixed(2)}</p>
                        
                        {rentalAccessories.length > 0 && (
                          <div style={{ marginTop: '8px', marginLeft: '16px' }}>
                            <p><strong>Accesorios:</strong></p>
                            {rentalAccessories.map((acc, accIndex) => {
                              const accPrice = parseFloat(acc.finalPrice) || 0;
                              return (
                                <p key={accIndex} style={{ marginLeft: '8px', color: '#6b7280', fontSize: '13px' }}>
                                  • {acc.accessory?.name || 'Accesorio'} - €{accPrice.toFixed(2)}
                                </p>
                              );
                            })}
                            <p style={{ marginTop: '4px', marginLeft: '8px', fontWeight: '500' }}>
                              Subtotal accesorios: €{rentalAccessoriesTotal.toFixed(2)}
                            </p>
                          </div>
                        )}
                        
                        <p style={{ marginTop: '8px', fontWeight: '600', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                          <strong>Subtotal línea (bici + accesorios):</strong> €{rentalTotal.toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>
                
                <div className="detail-section">
                  <h3>Desglose de Precios</h3>
                  <div className="price-breakdown">
                    {(() => {
                      const calculatedAccessoriesTotal = selectedBooking.vehicleRentals?.reduce((sum, rental) => {
                        const rentalAccessories = rental.accessories || [];
                        const rentalAccessoriesSum = rentalAccessories.reduce((accSum, acc) => {
                          return accSum + (parseFloat(acc.finalPrice) || 0);
                        }, 0);
                        return sum + rentalAccessoriesSum;
                      }, 0) || 0;

                      const insuranceFromLines =
                        selectedBooking.vehicleRentals?.reduce((sum, rental) => {
                          const rentalAccessories = rental.accessories || [];
                          return (
                            sum +
                            rentalAccessories.reduce((accSum, acc) => {
                              const cat = acc.accessory?.category;
                              const isIns =
                                cat === 'INSURANCE' || String(cat).toUpperCase() === 'INSURANCE';
                              if (isIns) {
                                return accSum + (parseFloat(acc.finalPrice) || 0);
                              }
                              return accSum;
                            }, 0)
                          );
                        }, 0) || 0;

                      const hasRentalAccessoryRows =
                        selectedBooking.vehicleRentals?.some((r) => (r.accessories || []).length > 0) ?? false;
                      const useLineSplit = hasRentalAccessoryRows;

                      // Misma regla que invoicing.service: con líneas RentalAccessory usar categorías; si no, agregados Booking
                      const calculatedInsuranceTotal = useLineSplit
                        ? insuranceFromLines
                        : selectedBooking.insuranceTotal != null && selectedBooking.insuranceTotal > 0
                          ? parseFloat(selectedBooking.insuranceTotal)
                          : insuranceFromLines;
                      const accessoriesOnlyTotal = useLineSplit
                        ? calculatedAccessoriesTotal - calculatedInsuranceTotal
                        : Math.max(
                            0,
                            (parseFloat(selectedBooking.accessoriesTotal) || 0) - calculatedInsuranceTotal
                          );
                      
                      const vehiclesTotal = vehiclesSubtotalDisplay;
                      const deliveryCost = parseFloat(selectedBooking.deliveryCost) || 0;
                      const discount = parseFloat(selectedBooking.discount) || 0;
                      const bookingTotalNum = parseFloat(selectedBooking.total) || 0;
                      const subtotalBeforeDiscount =
                        discount > 0.001 ? bookingTotalNum + discount : 0;
                      const discountPctDisplay =
                        subtotalBeforeDiscount > 0.001
                          ? Math.round((discount / subtotalBeforeDiscount) * 1000) / 10
                          : null;
                      
                      return (
                        <>
                          <div className="price-row">
                            <span>Subtotal vehículos:</span>
                            <span>€{vehiclesTotal.toFixed(2)}</span>
                          </div>
                          {accessoriesOnlyTotal > 0 && (
                            <div className="price-row">
                              <span>Subtotal accesorios:</span>
                              <span>€{accessoriesOnlyTotal.toFixed(2)}</span>
                            </div>
                          )}
                          {calculatedInsuranceTotal > 0 && (
                            <div className="price-row">
                              <span>Subtotal seguros:</span>
                              <span>€{calculatedInsuranceTotal.toFixed(2)}</span>
                            </div>
                          )}
                          {(selectedBooking.deliveryType === 'DELIVERY' ||
                            selectedBooking.deliveryType === 'HOTEL') && (
                            <div className="price-row">
                              <span>Suplemento entrega a domicilio:</span>
                              <span>€{deliveryCost.toFixed(2)}</span>
                            </div>
                          )}
                          {discount > 0 && (
                            <div className="price-row discount">
                              <span>
                                {discountPctDisplay != null && discountPctDisplay > 0
                                  ? `Descuento manual (${discountPctDisplay}%):`
                                  : 'Descuento:'}
                              </span>
                              <span>-€{discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="price-row total">
                            <span><strong>Total:</strong></span>
                            <span><strong>€{selectedBooking.total?.toFixed(2) || '0.00'}</strong></span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                    </>
                  );
                })()}

                {/* Sección de Contratos Firmados */}
                <div className="detail-section">
                  <h3>Contratos Firmados</h3>
                  {(() => {
                    const signedContracts = selectedBooking.contracts?.filter(c => c.status === 'SIGNED') || [];
                    const contractsToShow = signedContracts.length > 0 ? signedContracts : bookingContracts.filter(c => c.status === 'SIGNED');
                    
                    if (loadingContracts) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px' }}>
                          <Loader size={16} />
                          <span>Cargando contratos...</span>
                        </div>
                      );
                    }
                    
                    if (contractsToShow.length === 0) {
                      return (
                        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                          No hay contratos firmados para esta reserva
                        </p>
                      );
                    }
                    
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {contractsToShow.map((contract) => (
                          <div 
                            key={contract.id} 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              padding: '12px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <FileText size={20} color="#059669" />
                              <div>
                                <p style={{ margin: 0, fontWeight: 500 }}>
                                  Reserva #{selectedBooking.id} - Contrato #{contract.id}
                                  {contract.vehicle && (
                                    <span style={{ color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                                      - {contract.vehicle.model?.name} ({contract.vehicle.serialNumber})
                                    </span>
                                  )}
                                </p>
                                {contract.signedAt && (
                                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                                    Firmado: {new Date(contract.signedAt).toLocaleDateString('es-ES', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="btn-secondary"
                                onClick={() => viewSignedContract(contract)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px' }}
                                title="Visualizar contrato"
                              >
                                <Eye size={16} />
                                Ver
                              </button>
                              <button
                                className="btn-primary"
                                onClick={() => downloadSignedContract(contract)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px' }}
                                title="Descargar contrato"
                              >
                                <Download size={16} />
                                Descargar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div
                className="form-actions"
                style={{
                  marginTop: '12px',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}
              >
                <button type="button" className="btn-secondary" onClick={() => setShowViewModal(false)}>
                  Cerrar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={openCreateReservationFromViewedBooking}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  title="Abre el flujo de nueva reserva con los datos de contacto de este cliente"
                >
                  <UserPlus size={18} aria-hidden />
                  Nueva reserva con este cliente
                </button>
                {user?.role !== 'AGENCY' && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      const id = selectedBooking.id;
                      setShowViewModal(false);
                      handleEditBooking({ id });
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Edit size={18} aria-hidden />
                    Editar reserva
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && selectedBooking && (
        <Fragment>
        <div className="modal-overlay edit-booking-modal-root">
          <div className="modal modal-booking-wide modal-booking-edit-fullscreen">
            <div className="modal-header">
              <h2>Editar Reserva #{selectedBooking.id}</h2>
              <button type="button" onClick={closeEditModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <form className="edit-form">
                {(() => {
                  const endDate = selectedBooking.endDateTime ? new Date(selectedBooking.endDateTime) : null;
                  const isPastBooking = endDate && endDate < new Date();

                  if (isPastBooking) {
                    return (
                      <>
                        <p className="edit-form-notice">Esta reserva ya ha pasado. Solo puedes modificar el campo de notas.</p>
                        <div className="form-group">
                          <label>Notas</label>
                          <textarea
                            value={editForm.notes ?? ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                            rows={5}
                          />
                        </div>
                      </>
                    );
                  }

                  if (selectedBooking.status === 'CANCELLED') {
                    return (
                      <>
                        <p className="edit-form-notice">Esta reserva está cancelada. Solo puedes modificar el estado de abono.</p>
                        <div className="form-group">
                          <label>Estado de abono</label>
                          <select
                            value={editForm.refundStatus || 'PENDING_REFUND'}
                            onChange={(e) => setEditForm(prev => ({ ...prev, refundStatus: e.target.value }))}
                          >
                            <option value="PENDING_REFUND">Pendiente de abono</option>
                            <option value="REFUNDED">Abonada</option>
                          </select>
                        </div>
                      </>
                    );
                  }

                  const isBikeFleetBooking = selectedBooking.activityId == null;
                  const accessoryDirty = isAccessoryDraftDirty(selectedBooking, accessoryDraftByRentalId);
                  const deliveryDirty = isDeliveryFormDirty(selectedBooking, editForm);
                  const scheduleDirty = isScheduleDraftDirty(selectedBooking, scheduleDraftStart, scheduleDraftEnd);
                  const previewBreakdown = computeDraftPriceBreakdown(
                    selectedBooking,
                    editForm,
                    accessoryDraftByRentalId
                  );
                  const officialTotal = Number(selectedBooking.total);
                  const showLocalPricePreview = accessoryDirty || deliveryDirty;
                  const priceEditBlockedFlag = priceEditBlocked(
                    selectedBooking,
                    editForm,
                    accessoryDraftByRentalId
                  );
                  const priceEditBlockedText = priceEditBlockMessage(
                    selectedBooking,
                    editForm,
                    accessoryDraftByRentalId
                  );
                  const showPricePreviewSafe = showLocalPricePreview && !priceEditBlockedFlag;

                  return (
                    <>
                      <div className="edit-booking-tabs" role="tablist" aria-label="Secciones de edición de reserva">
                        <button
                          type="button"
                          role="tab"
                          className={`edit-booking-tab${editModalTab === 'EDIT' ? ' is-active' : ''}`}
                          aria-selected={editModalTab === 'EDIT'}
                          onClick={() => setEditModalTab('EDIT')}
                        >
                          Edición
                        </button>
                        <button
                          type="button"
                          role="tab"
                          className={`edit-booking-tab${editModalTab === 'FINANCIAL' ? ' is-active' : ''}`}
                          aria-selected={editModalTab === 'FINANCIAL'}
                          onClick={() => setEditModalTab('FINANCIAL')}
                        >
                          Cobros y facturación
                        </button>
                        <button
                          type="button"
                          role="tab"
                          className={`edit-booking-tab${editModalTab === 'LEDGER' ? ' is-active' : ''}`}
                          aria-selected={editModalTab === 'LEDGER'}
                          onClick={() => setEditModalTab('LEDGER')}
                        >
                          Historial
                        </button>
                      </div>

                      {user?.role !== 'AGENCY' && (
                        <div className="edit-booking-summary-strip">
                          <div className="edit-booking-summary-official">
                            <h3 className="edit-booking-summary-title">Totales guardados</h3>
                            <p className="edit-booking-summary-total">
                              Total reserva: <strong>{formatEur(officialTotal)}</strong>
                            </p>
                            <p className="edit-form-hint" style={{ marginTop: 6 }}>
                              Este importe no cambia hasta que apliques accesorios/entrega o acciones que actualicen el
                              precio en el servidor. Emite factura tras confirmar cobros cuando corresponda.
                            </p>
                          </div>
                          {showPricePreviewSafe && (
                            <div className="edit-booking-summary-preview" aria-live="polite">
                              <h3 className="edit-booking-summary-title">Vista previa (sin guardar)</h3>
                              <ul className="edit-booking-preview-lines">
                                <li>
                                  <span>Vehículos (líneas)</span>
                                  <span>{formatEur(previewBreakdown.vehicles)}</span>
                                </li>
                                <li>
                                  <span>Accesorios (borrador)</span>
                                  <span>{formatEur(previewBreakdown.accessories)}</span>
                                </li>
                                {(editForm.deliveryType === 'DELIVERY' || editForm.deliveryType === 'HOTEL') && (
                                  <li>
                                    <span>Entrega</span>
                                    <span>{formatEur(previewBreakdown.delivery)}</span>
                                  </li>
                                )}
                                {previewBreakdown.discount > 0 && (
                                  <li>
                                    <span>Descuento</span>
                                    <span>−{formatEur(previewBreakdown.discount)}</span>
                                  </li>
                                )}
                                <li className="edit-booking-preview-total">
                                  <span>Total estimado</span>
                                  <strong>{formatEur(previewBreakdown.totalPreview)}</strong>
                                </li>
                              </ul>
                              <p className="edit-form-hint">
                                Al pulsar «Aplicar accesorios y entrega» el servidor recalcula el total oficial y el
                                pendiente. Operaciones de fechas, flota o cambio de bici actualizan el total al
                                confirmarlas.
                              </p>
                            </div>
                          )}
                          {priceEditBlockedFlag && (
                            <div className="edit-booking-mixed-intent-blocked" role="alert">
                              <p className="edit-form-hint" style={{ margin: 0 }}>
                                {priceEditBlockedText}
                              </p>
                            </div>
                          )}
                          {(scheduleDirty || accessoryDirty || deliveryDirty) && (
                            <p className="edit-booking-pending-hint">
                              {scheduleDirty && <span>Cambios de fechas sin aplicar. </span>}
                              {accessoryDirty && <span>Accesorios sin aplicar. </span>}
                              {deliveryDirty && <span>Entrega sin aplicar. </span>}
                            </p>
                          )}
                        </div>
                      )}

                      {editModalTab === 'EDIT' && (
                      <div className="edit-booking-section">
                        <h3 className="edit-booking-section-title">
                          <User size={18} aria-hidden />
                          Cliente y estado
                        </h3>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Estado</label>
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                            >
                              <option value="PENDING">Pendiente</option>
                              <option value="CONFIRMED">Confirmada</option>
                              <option value="CANCELLED">Cancelada</option>
                              <option value="COMPLETED">Completada</option>
                            </select>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label className="edit-form-checkbox" htmlFor="edit-booking-no-show">
                              <input
                                id="edit-booking-no-show"
                                type="checkbox"
                                checked={!!editForm.noShow}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setEditForm((prev) => ({
                                    ...prev,
                                    noShow: checked,
                                    status: checked ? 'CANCELLED' : prev.status,
                                    // Si es no-show, no hay abono según política: no enviar refundStatus.
                                    refundStatus: checked ? '' : prev.refundStatus,
                                  }));
                                }}
                              />
                              <span className="edit-form-checkbox__label">No show (cancelada sin abono)</span>
                            </label>
                            <p className="edit-form-hint" style={{ marginTop: 6 }}>
                              Marca esta opción para cancelar la reserva sin iniciar reembolso (según política de no-show).
                            </p>
                          </div>
                        </div>
                        {editForm.status === 'CANCELLED' && !editForm.noShow && (
                          <div className="form-row">
                            <div className="form-group">
                              <label>Estado de abono</label>
                              <select
                                value={editForm.refundStatus || 'PENDING_REFUND'}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, refundStatus: e.target.value }))}
                              >
                                <option value="PENDING_REFUND">Pendiente de abono</option>
                                <option value="REFUNDED">Abonada</option>
                              </select>
                              {isStripePayment(selectedBooking) ? (
                                <p className="edit-form-hint">
                                  Reserva con tarjeta (Stripe): el reembolso del 100% se realizará automáticamente al
                                  guardar.
                                </p>
                              ) : (
                                <p className="edit-form-hint">
                                  La devolución del importe es manual. Marca &quot;Abonada&quot; cuando hayas realizado el
                                  reembolso.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="form-row">
                          <div className="form-group">
                            <label>Nombre del cliente</label>
                            <input
                              type="text"
                              value={editForm.customerName}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, customerName: e.target.value }))}
                            />
                          </div>
                          <div className="form-group">
                            <label>Email</label>
                            <input
                              type="email"
                              value={editForm.customerEmail}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, customerEmail: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Teléfono</label>
                          <input
                            type="tel"
                            value={editForm.customerPhone}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, customerPhone: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Notas</label>
                          <textarea
                            value={editForm.notes ?? ''}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <p className="edit-form-hint">
                          Usa el botón inferior «Guardar datos de cliente y estado» para persistir estos campos. Los
                          importes se confirman con «Aplicar accesorios y entrega» o con fechas / flota (cada una con su
                          acción en servidor).
                        </p>
                      </div>
                      )}


                      {user?.role !== 'AGENCY' && isBikeFleetBooking && (
                        editModalTab === 'EDIT' && (
                        <div className="edit-booking-section">
                          <h3 className="edit-booking-section-title">
                            <Calendar size={18} aria-hidden />
                            Fechas del alquiler
                          </h3>
                          <p className="edit-form-hint">
                            Cambiar inicio o fin recalcula días, precio de bicis y accesorios por día, y sincroniza los
                            bloques de disponibilidad. Usa este bloque en lugar de guardar fechas en «Guardar cambios».
                          </p>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Inicio</label>
                              <div className="schedule-datetime-row">
                                <SingleDatePicker
                                  value={splitDatetimeLocal(scheduleDraftStart).date}
                                  onChange={(d) => setScheduleDraftStart(joinDatetimeLocal(d, splitDatetimeLocal(scheduleDraftStart).time))}
                                  closedDates={scheduleClosedDays}
                                  className="schedule-date-trigger"
                                  disabled={
                                    applyingSchedule ||
                                    savingBikeLines ||
                                    savingDeliveryApply ||
                                    savingApplyLinesAndDelivery ||
                                    savingApplyLinesAndEmitQuipu
                                  }
                                />
                                <input
                                  type="time"
                                  value={splitDatetimeLocal(scheduleDraftStart).time}
                                  onChange={(e) => setScheduleDraftStart(joinDatetimeLocal(splitDatetimeLocal(scheduleDraftStart).date, e.target.value))}
                                  disabled={
                                    applyingSchedule ||
                                    savingBikeLines ||
                                    savingDeliveryApply ||
                                    savingApplyLinesAndDelivery ||
                                    savingApplyLinesAndEmitQuipu
                                  }
                                />
                              </div>
                            </div>
                            <div className="form-group">
                              <label>Fin</label>
                              <div className="schedule-datetime-row">
                                <SingleDatePicker
                                  value={splitDatetimeLocal(scheduleDraftEnd).date}
                                  onChange={(d) => setScheduleDraftEnd(joinDatetimeLocal(d, splitDatetimeLocal(scheduleDraftEnd).time))}
                                  closedDates={scheduleClosedDays}
                                  className="schedule-date-trigger"
                                  disabled={
                                    applyingSchedule ||
                                    savingBikeLines ||
                                    savingDeliveryApply ||
                                    savingApplyLinesAndDelivery ||
                                    savingApplyLinesAndEmitQuipu
                                  }
                                />
                                <input
                                  type="time"
                                  value={splitDatetimeLocal(scheduleDraftEnd).time}
                                  onChange={(e) => setScheduleDraftEnd(joinDatetimeLocal(splitDatetimeLocal(scheduleDraftEnd).date, e.target.value))}
                                  disabled={
                                    applyingSchedule ||
                                    savingBikeLines ||
                                    savingDeliveryApply ||
                                    savingApplyLinesAndDelivery ||
                                    savingApplyLinesAndEmitQuipu
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={applyScheduleChangeRequest}
                            disabled={
                              applyingSchedule ||
                              savingBikeLines ||
                              savingDeliveryApply ||
                              savingApplyLinesAndDelivery ||
                              savingApplyLinesAndEmitQuipu
                            }
                          >
                            {applyingSchedule ? <Loader size={16} /> : null}
                            {applyingSchedule ? 'Aplicando fechas…' : 'Aplicar nuevas fechas'}
                          </button>
                        </div>
                        )
                      )}

                      {user?.role !== 'AGENCY' && isBikeFleetBooking && (
                        editModalTab === 'EDIT' && (
                        <div className="edit-booking-section">
                          <h3 className="edit-booking-section-title">
                            <Plus size={18} aria-hidden />
                            Añadir otra bicicleta
                          </h3>
                          <p className="edit-form-hint">
                            Lista las bicis libres en las fechas <strong>actuales</strong> de la reserva. Si cambias las
                            fechas, aplica primero el bloque anterior y vuelve a cargar la lista.
                          </p>
                          <div className="edit-booking-vehicle-select-row edit-booking-vehicle-select-row--align-start">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={loadAddBikeVehicles}
                              disabled={
                                addBikeLoading ||
                                addingVehicle ||
                                applyingSchedule ||
                                savingBikeLines ||
                                savingDeliveryApply ||
                                savingApplyLinesAndDelivery ||
                                savingApplyLinesAndEmitQuipu
                              }
                            >
                              {addBikeLoading ? <Loader size={16} /> : null}
                              {addBikeLoading ? 'Cargando…' : 'Cargar disponibles'}
                            </button>
                            <div className="edit-booking-add-bike-combo">
                              <input
                                type="search"
                                className="edit-booking-add-bike-search"
                                placeholder={
                                  addBikeList.length === 0
                                    ? 'Primero pulsa «Cargar disponibles»'
                                    : 'Buscar modelo o número de serie…'
                                }
                                value={addBikeSearchQuery}
                                onChange={(e) => {
                                  setAddBikeSearchQuery(e.target.value);
                                  setAddBikeComboboxOpen(true);
                                }}
                                onFocus={() => {
                                  if (addBikeList.length > 0) setAddBikeComboboxOpen(true);
                                }}
                                onBlur={() => {
                                  window.setTimeout(() => setAddBikeComboboxOpen(false), 200);
                                }}
                                disabled={addBikeList.length === 0 || addingVehicle}
                                autoComplete="off"
                                aria-autocomplete="list"
                                aria-expanded={addBikeComboboxOpen && addBikeList.length > 0}
                              />
                              {addBikeComboboxOpen && addBikeList.length > 0 && (
                                <ul className="edit-booking-add-bike-list" role="listbox">
                                  {addBikeListFiltered.length === 0 ? (
                                    <li className="edit-booking-add-bike-list-empty" role="presentation">
                                      Sin coincidencias
                                    </li>
                                  ) : (
                                    addBikeListFiltered.map((v) => (
                                      <li
                                        key={v.id}
                                        role="option"
                                        aria-selected={String(selectedAddVehicleId) === String(v.id)}
                                        className={
                                          String(selectedAddVehicleId) === String(v.id) ? 'is-selected' : ''
                                        }
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                          setSelectedAddVehicleId(String(v.id));
                                          setAddBikeSearchQuery('');
                                          setAddBikeComboboxOpen(false);
                                        }}
                                      >
                                        <span className="edit-booking-add-bike-list-model">
                                          {v._modelName || 'Modelo'}
                                        </span>
                                        <span className="edit-booking-add-bike-list-meta">
                                          SN {v.serialNumber} · {v.size} · #{v.id}
                                        </span>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              )}
                              {selectedAddVehicleId ? (
                                <div className="edit-booking-add-bike-selected">
                                  <span>
                                    <strong>Selección:</strong>{' '}
                                    {(() => {
                                      const vs = addBikeList.find(
                                        (x) => String(x.id) === String(selectedAddVehicleId)
                                      );
                                      return vs
                                        ? `${vs._modelName || 'Modelo'} — ${vs.serialNumber} (#${vs.id})`
                                        : `#${selectedAddVehicleId}`;
                                    })()}
                                  </span>
                                  <button
                                    type="button"
                                    className="edit-booking-add-bike-clear"
                                    onClick={() => setSelectedAddVehicleId('')}
                                    disabled={addingVehicle}
                                  >
                                    Quitar
                                  </button>
                                </div>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={addVehicleToBookingRequest}
                              disabled={!selectedAddVehicleId || addingVehicle}
                            >
                              {addingVehicle ? <Loader size={16} /> : null}
                              {addingVehicle ? 'Añadiendo…' : 'Añadir a la reserva'}
                            </button>
                          </div>
                        </div>
                        )
                      )}

                      {/* Bicicletas: sustitución, accesorios y baja */}
                      {editModalTab === 'EDIT' && (
                      <div className="edit-booking-section">
                        <h3 className="edit-booking-section-title">
                          <Package size={18} aria-hidden />
                          Bicicletas y accesorios por línea
                        </h3>
                        <p className="edit-form-hint">
                          Sustituye la unidad por otra del mismo modelo, ajusta accesorios o quita una línea (si hay más de
                          una). Los accesorios se confirman con «Aplicar accesorios y entrega» más abajo.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {(selectedBooking.vehicleRentals || []).map((rental) => {
                            const rentalId = rental.id;
                            const currentVehicle = rental.vehicle;
                            const currentModelName = currentVehicle?.model?.name || 'Modelo';
                            const currentSerial = currentVehicle?.serialNumber || 'N/A';
                            const currentSize = currentVehicle?.size || 'N/A';
                            const options = replacementOptionsByRentalId[rentalId] || [];
                            const isLoading = !!loadingReplacementByRentalId[rentalId];
                            const isChanging = !!changingBikeByRentalId[rentalId];
                            const selectedNew = selectedReplacementVehicleByRentalId[rentalId] || '';

                            return (
                              <div
                                key={rentalId}
                                style={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '10px 12px',
                                  background: '#f9fafb'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div style={{ fontSize: '13px', color: '#374151' }}>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>
                                      {currentModelName} ({currentSize})
                                    </div>
                                    <div style={{ color: '#6b7280' }}>
                                      Actual: {currentSerial} · ID #{currentVehicle?.id}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {user?.role !== 'AGENCY' &&
                                      isBikeFleetBooking &&
                                      (selectedBooking.vehicleRentals || []).length > 1 && (
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          title="Quitar esta bicicleta de la reserva"
                                          onClick={() => removeVehicleRentalRequest(rentalId)}
                                          disabled={
                                            !!removingRentalId ||
                                            isLoading ||
                                            isChanging ||
                                            addingVehicle ||
                                            applyingSchedule
                                          }
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                        >
                                          {removingRentalId === rentalId ? (
                                            <Loader size={16} />
                                          ) : (
                                            <Trash2 size={16} aria-hidden />
                                          )}
                                          Quitar
                                        </button>
                                      )}
                                    <button
                                      type="button"
                                      className="btn-secondary"
                                      onClick={() => loadReplacementVehicles(selectedBooking.id, rentalId)}
                                      disabled={isLoading || isChanging}
                                    >
                                      {isLoading ? <Loader size={16} /> : null}
                                      {isLoading ? 'Cargando...' : 'Ver disponibles'}
                                    </button>
                                  </div>
                                </div>

                                <div
                                  className="edit-booking-vehicle-select-row"
                                  style={{ marginTop: '10px' }}
                                >
                                  <select
                                    value={selectedNew}
                                    onChange={(e) => setSelectedReplacementVehicleByRentalId(prev => ({ ...prev, [rentalId]: e.target.value }))}
                                    disabled={isChanging || options.length === 0}
                                    className="edit-booking-vehicle-select edit-booking-vehicle-select--replacement"
                                  >
                                    <option value="">
                                      {options.length === 0 ? 'Cargar disponibles para seleccionar' : 'Selecciona una bicicleta'}
                                    </option>
                                    {options.map((v) => (
                                      <option key={v.id} value={v.id}>
                                        #{v.id} · {v.serialNumber} · {v.size}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => requestApplyReplacementVehicle(selectedBooking.id, rentalId)}
                                    disabled={!selectedNew || isChanging}
                                  >
                                    {isChanging ? <Loader size={16} /> : null}
                                    {isChanging ? 'Cambiando...' : 'Aplicar cambio'}
                                  </button>
                                </div>

                                {user?.role !== 'AGENCY' &&
                                  (() => {
                                    const mid = rental.vehicle?.modelId;
                                    const catalog = mid != null ? accessoriesByModelEdit[mid] : undefined;
                                    const list = Array.isArray(catalog) ? catalog : [];
                                    const draft = accessoryDraftByRentalId[rental.id] || [];
                                    return (
                                      <div
                                        className="edit-accessory-rental-block"
                                        style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}
                                      >
                                        <div className="edit-accessory-rental-title">Accesorios y seguros</div>
                                        {mid == null && (
                                          <p className="edit-form-hint">
                                            No hay modelo asociado; no se puede cargar el catálogo.
                                          </p>
                                        )}
                                        {mid != null && catalog == null && (
                                          <p className="edit-form-hint">Cargando accesorios…</p>
                                        )}
                                        {catalog != null && list.length === 0 && (
                                          <p className="edit-form-hint">No hay accesorios en catálogo para este modelo.</p>
                                        )}
                                        {catalog != null && list.length > 0 && (
                                          <div className="edit-accessory-chips">
                                            {list.map((acc) => {
                                              const selected = draft.some(
                                                (x) => Number(x.accessoryId) === Number(acc.id)
                                              );
                                              const duration = computeDurationDays(
                                                selectedBooking.startDateTime,
                                                selectedBooking.endDateTime
                                              );
                                              const linePrice =
                                                acc.accessoryPriceType === 'DAILY'
                                                  ? Number(acc.price) * duration
                                                  : Number(acc.price);
                                              return (
                                                <label
                                                  key={acc.id}
                                                  className={`edit-accessory-chip${selected ? ' selected' : ''}`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => toggleAccessoryDraft(rental.id, acc)}
                                                  />
                                                  <span>{acc.name}</span>
                                                  <span className="edit-accessory-price">{formatEur(linePrice)}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {editAccessoryInlineErrorByRentalId?.[rental.id] && (
                                          <div
                                            className="edit-accessory-inline-error"
                                            role="alert"
                                            aria-live="polite"
                                          >
                                            {editAccessoryInlineErrorByRentalId[rental.id]}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}

                                {options.length === 0 && !isLoading && (
                                  <p className="edit-form-hint" style={{ marginTop: '8px' }}>
                                    Se mostrarán solo bicicletas del mismo modelo disponibles en las fechas de la reserva.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="edit-form-hint">
                          Al cambiar de bicicleta se liberan los slots de la unidad anterior y se reservan los de la nueva.
                        </p>
                      </div>
                      )}

                      {editModalTab === 'EDIT' && (
                      <div className="edit-booking-section">
                        <h3 className="edit-booking-section-title">Tipo de entrega y suplemento</h3>
                        {user?.role === 'AGENCY' ? (
                          <p className="edit-form-hint">Solo consulta; la agencia no puede aplicar cambios aquí.</p>
                        ) : (
                          <p className="edit-form-hint">
                            Elige entrega en tienda o a domicilio. La vista previa arriba se actualiza al editar; el total
                            oficial en servidor cambia al pulsar «Aplicar accesorios y entrega».
                          </p>
                        )}
                        <div className="form-row">
                          <div className="form-group">
                            <label>Tipo de entrega</label>
                            <select
                              value={editForm.deliveryType}
                              disabled={user?.role === 'AGENCY'}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEditForm((prev) => {
                                  const next = {
                                    ...prev,
                                    deliveryType: v,
                                    ...(v === 'PICKUP' ? { deliveryCost: '0', hotelAddress: '' } : {}),
                                  };
                                  // Importante: permitir siempre editar el borrador (p. ej. pasar de entrega a tienda).
                                  // Las reglas de mezcla/subida-bajada se aplican al confirmar “Aplicar…”, no al teclear.
                                  const msg = priceEditBlockMessage(selectedBooking, next, accessoryDraftByRentalId);
                                  queueMicrotask(() => setError(msg || null));
                                  return next;
                                });
                              }}
                            >
                              <option value="PICKUP">En tienda</option>
                              <option value="DELIVERY">Entrega a domicilio</option>
                            </select>
                          </div>
                        </div>

                        {editForm.deliveryType === 'DELIVERY' && (
                          <div className="form-row">
                            <div className="form-group">
                              <label>Suplemento entrega a domicilio (€)</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                disabled={user?.role === 'AGENCY'}
                                value={editForm.deliveryCost ?? '0'}
                                onChange={(e) =>
                                  setEditForm((prev) => {
                                    const next = { ...prev, deliveryCost: e.target.value };
                                    const msg = priceEditBlockMessage(selectedBooking, next, accessoryDraftByRentalId);
                                    queueMicrotask(() => setError(msg || null));
                                    return next;
                                  })
                                }
                              />
                              {user?.role !== 'AGENCY' && (
                                <p className="edit-form-hint">
                                  Se suma al total; el importe queda <strong>pendiente de cobro</strong> hasta que lo
                                  cobres. Tras aplicar, podrás emitir factura en Quipu desde el aviso o el bloque de
                                  abajo.
                                </p>
                              )}
                            </div>
                            <div className="form-group">
                              <label htmlFor="edit-booking-delivery-address">
                                Dirección de entrega <span className="edit-required-mark">*</span>
                              </label>
                              <input
                                id="edit-booking-delivery-address"
                                type="text"
                                required
                                disabled={user?.role === 'AGENCY'}
                                value={editForm.hotelAddress ?? ''}
                                onChange={(e) =>
                                  setEditForm((prev) => {
                                    const next = { ...prev, hotelAddress: e.target.value };
                                    const msg = priceEditBlockMessage(selectedBooking, next, accessoryDraftByRentalId);
                                    queueMicrotask(() => setError(msg || null));
                                    return next;
                                  })
                                }
                                placeholder="Calle, número, código postal, localidad…"
                                autoComplete="street-address"
                              />
                              <p className="edit-form-hint">Obligatoria si la entrega es a domicilio.</p>
                            </div>
                          </div>
                        )}

                        {editForm.deliveryType === 'DELIVERY' && selectedBooking && (
                          <p className="edit-form-hint" style={{ marginTop: '-4px' }}>
                            Total estimado tras aplicar (servidor igualará este cálculo):{' '}
                            <strong>
                              {formatEur(
                                (() => {
                                  const dcRaw = parseFloat(
                                    String(editForm.deliveryCost || '0').replace(',', '.')
                                  );
                                  const dc =
                                    Number.isFinite(dcRaw) && dcRaw >= 0 ? dcRaw : 0;
                                  return (
                                    Math.round(
                                      ((Number(selectedBooking.vehiclesTotal) || 0) +
                                        (Number(selectedBooking.accessoriesTotal) || 0) +
                                        dc -
                                        (Number(selectedBooking.discount) || 0)) *
                                        100
                                    ) / 100
                                  );
                                })()
                              )}
                            </strong>
                          </p>
                        )}

                      </div>
                      )}

                      {editModalTab === 'EDIT' && user?.role !== 'AGENCY' && (
                        <div className="edit-booking-section edit-booking-accessories-delivery-quipu">
                          <h3 className="edit-booking-section-title">
                            <Save size={18} aria-hidden />
                            Confirmar cambios de importe (accesorios y entrega)
                          </h3>
                          <p className="edit-form-hint">
                            Hasta que pulses aquí, el total oficial de la reserva no se actualiza en el servidor. Después
                            podrás emitir factura en Quipu y registrar cobros en el bloque «Cobros, facturación y saldos».
                          </p>
                          <div className="edit-supplement-quipu-actions-row">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={tryOpenConfirmApplyLinesAndDelivery}
                              disabled={
                                savingBikeLines ||
                                savingDeliveryApply ||
                                savingApplyLinesAndDelivery ||
                                savingApplyLinesAndEmitQuipu ||
                                priceEditBlockedFlag
                              }
                            >
                              {savingApplyLinesAndDelivery ? <Loader size={16} /> : null}
                              {savingApplyLinesAndDelivery
                                ? 'Aplicando…'
                                : 'Aplicar accesorios y entrega'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={tryOpenConfirmApplyLinesAndEmitQuipu}
                              disabled={
                                savingBikeLines ||
                                savingDeliveryApply ||
                                savingApplyLinesAndDelivery ||
                                savingApplyLinesAndEmitQuipu ||
                                priceEditBlockedFlag
                              }
                            >
                              {savingApplyLinesAndEmitQuipu ? <Loader size={16} /> : null}
                              {savingApplyLinesAndEmitQuipu
                                ? 'Aplicando y emitiendo…'
                                : 'Aplicar y emitir factura Quipu (pendiente)'}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {(() => {
                        const fin = selectedBooking.financials;
                        const total = Number(selectedBooking.total);
                        const paid = Number(fin?.paidTotal ?? 0);
                        const due = Number(fin?.balanceDue ?? 0);
                        const nextPay = Number(fin?.nextExpectedPaymentAmount ?? 0);
                        const credit = paid > total + 0.02 ? Math.round((paid - total) * 100) / 100 : 0;
                        const customerEmail = selectedBooking.customer?.email || selectedBooking.guestEmail || '';
                        const customerName = selectedBooking.customer?.fullName || selectedBooking.guestName || '';
                        const pendingSupplementDraftExtra = getPendingSupplementDraftExtraLines(
                          selectedBooking,
                          editForm,
                          accessoryDraftByRentalId
                        );
                        const draftEconomicDirty = pendingSupplementDraftExtra.hasUnsavedEconomicDraft;
                        /** Misma lógica con borrador o con datos ya guardados: detalle por bici/accesorios/entrega.
                         *  No usar solo GET pending-supplement-preview para la tabla: el backend reparte el pendiente
                         *  por buckets (imputación del cobrado) y no coincide con el desglose detallado. */
                        const cobrarSaldoDraftTable =
                          fin && selectedBooking
                            ? computeCobrarSaldoPendingDraftTable(
                                selectedBooking,
                                editForm,
                                accessoryDraftByRentalId,
                                paid,
                                resolveDraftAccessoryCategory
                              )
                            : null;
                        const dueDisplayed = draftEconomicDirty
                          ? cobrarSaldoDraftTable.pendienteEstimado
                          : due;
                        const showPay =
                          (nextPay > 0 ||
                            due > 0.02 ||
                            (draftEconomicDirty &&
                              cobrarSaldoDraftTable &&
                              cobrarSaldoDraftTable.pendienteEstimado > 0.02)) &&
                          selectedBooking.status !== 'CANCELLED';
                        const payAmount =
                          draftEconomicDirty
                            ? cobrarSaldoDraftTable.pendienteEstimado
                            : nextPay > 0
                              ? nextPay
                              : due;
                        const useDraftPendingSupplementLines = draftEconomicDirty;
                        const supplementBreakdownLines = cobrarSaldoDraftTable?.rows || [];
                        const supplementBreakdownFooterAmount =
                          cobrarSaldoDraftTable?.pendienteEstimado ?? dueDisplayed;
                        const hasSupplementBreakdownForManualPay = payAmount > 0.02;

                        const amountForQuipuOrientative = (() => {
                          if (showPartialSupplementAmount) {
                            const raw = String(supplementQuipuAmount || '').trim().replace(',', '.');
                            if (raw !== '') {
                              const n = parseFloat(raw);
                              if (Number.isFinite(n) && n > 0) {
                                return Math.round(n * 100) / 100;
                              }
                            }
                          }
                          return payAmount;
                        })();
                        const quipuFiscalPreview =
                          draftEconomicDirty && amountForQuipuOrientative > 0.02
                            ? computeQuipuSupplementOrientativeLines(
                                selectedBooking,
                                editForm,
                                accessoryDraftByRentalId,
                                amountForQuipuOrientative,
                                resolveDraftAccessoryCategory
                              )
                            : !draftEconomicDirty &&
                                (pendingSupplementPreview?.lines || []).length > 0 &&
                                amountForQuipuOrientative > 0.02
                              ? {
                                  lines: pendingSupplementPreview.lines,
                                  totalEur:
                                    pendingSupplementPreview.amountEur != null
                                      ? pendingSupplementPreview.amountEur
                                      : amountForQuipuOrientative,
                                }
                              : null;

                        const abonoHints = getCreditAbonoPaymentHints(selectedBooking.payments);
                        const abonoOptions = buildCreditAbonoMethodOptions(abonoHints);

                        const refundPreviewIfApply =
                          showPricePreviewSafe
                            ? Math.max(
                                0,
                                Math.round((paid - previewBreakdown.totalPreview) * 100) / 100
                              )
                            : 0;

                        const creditDownLinesForRefundPreview =
                          refundPreviewIfApply > 0.02
                            ? computeDraftCreditDownLines(
                                selectedBooking,
                                editForm,
                                accessoryDraftByRentalId
                              ).lines
                            : [];

                        const saldoFavorLedgerLines =
                          credit > 0.02
                            ? parseLedgerSaldoFavorDetailLines(selectedBooking.ledgerEntries)
                            : [];

                        const officialBr = getOfficialBookingPriceBreakdown(selectedBooking);

                        if (editModalTab === 'LEDGER') {
                          return (
                            <div className="edit-booking-section edit-booking-ledger">
                              <h3 className="edit-booking-section-title">
                                <Eye size={18} aria-hidden />
                                Historial de cambios (ledger)
                              </h3>
                              {selectedBooking.ledgerEntries && selectedBooking.ledgerEntries.length > 0 ? (
                                <>
                                  <p className="edit-form-hint" style={{ marginTop: -6 }}>
                                    Últimos movimientos guardados. Muestra fecha/hora, cambio y usuario (si está disponible).
                                  </p>
                                  <div className="edit-ledger-table-wrap">
                                    <table className="edit-ledger-table">
                                      <thead>
                                        <tr>
                                          <th>Fecha</th>
                                          <th>Cambio</th>
                                          <th>Usuario</th>
                                          <th>Detalle</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[...selectedBooking.ledgerEntries]
                                          .slice(-50)
                                          .reverse()
                                          .map((row) => {
                                            const action =
                                              (row?.payload && typeof row.payload === 'object' && row.payload.action) ||
                                              row.eventType ||
                                              '—';
                                            const actor =
                                        row.actorLabel ||
                                        row.actorUser?.name ||
                                        row.actorUser?.email ||
                                        (row.actorUserId != null ? `Usuario #${row.actorUserId}` : 'Sistema');
                                            let detail = '';
                                            if (row.paymentId) detail += `Pago #${row.paymentId}. `;
                                            if (row.payload) {
                                              try {
                                                detail +=
                                                  typeof row.payload === 'object'
                                                    ? JSON.stringify(row.payload)
                                                    : String(row.payload);
                                              } catch {
                                                detail += '[payload no serializable]';
                                              }
                                            }
                                            if (detail.length > 320) detail = `${detail.slice(0, 320)}…`;

                                            return (
                                              <tr key={row.id}>
                                                <td className="edit-ledger-cell-date">{formatDateTime(row.createdAt)}</td>
                                                <td className="edit-ledger-cell-action">{action}</td>
                                                <td className="edit-ledger-cell-actor">{actor}</td>
                                                <td className="edit-ledger-cell-detail">{detail || '—'}</td>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              ) : (
                                <p className="edit-form-hint">Aún no hay movimientos en el ledger para esta reserva.</p>
                              )}
                            </div>
                          );
                        }

                        if (editModalTab !== 'FINANCIAL') return null;

                        const hasQuipuHistory = bookingHasPaidInvoicedQuipu(selectedBooking);

                        return (
                          <div className="edit-booking-section edit-booking-financial">
                            <h3 className="edit-booking-section-title">Cobros, facturación y saldos</h3>
                            <p className="edit-form-hint" style={{ marginBottom: 12 }}>
                              El total guardado coincide con el desglose siguiente (se actualiza al aplicar fechas, flota,
                              accesorios o entrega en el servidor). Debajo gestionas cobros pendientes, Quipu y abonos.
                            </p>
                            {user?.role !== 'AGENCY' && (
                              <div className="edit-financial-dual-ops-notice">
                                <strong>Subidas y bajadas por separado</strong>
                                <p>
                                  En la misma edición solamente se debe realizar una única operación: subir (pago suplementario) o bajar (abono) el total de la reserva.
                                </p>
                                {/* {hasQuipuHistory && (
                                  <p style={{ marginTop: 10, marginBottom: 0 }}>
                                    <strong>Con facturas Quipu:</strong> además es requisito fiscal: rectificativa/abono
                                    por lo que deja de aplicarse y <strong>otra</strong> factura o suplemento por el nuevo
                                    importe. Completa la rectificativa cuando corresponda y emite el suplemento desde este
                                    bloque o tras aplicar.
                                  </p>
                                )} */}
                              </div>
                            )}
                            <div className="edit-booking-price-breakdown-block">
                              <h4 className="edit-booking-price-breakdown-title">Desglose del precio (servidor)</h4>
                              <div className="price-breakdown edit-booking-price-breakdown">
                                <div className="price-row">
                                  <span>Subtotal vehículos</span>
                                  <span>{formatEur(officialBr.vehiclesTotal)}</span>
                                </div>
                                {officialBr.accessoriesOnlyTotal > 0 && (
                                  <div className="price-row">
                                    <span>Subtotal accesorios</span>
                                    <span>{formatEur(officialBr.accessoriesOnlyTotal)}</span>
                                  </div>
                                )}
                                {officialBr.insuranceTotal > 0 && (
                                  <div className="price-row">
                                    <span>Subtotal seguros</span>
                                    <span>{formatEur(officialBr.insuranceTotal)}</span>
                                  </div>
                                )}
                                {officialBr.showDelivery && (
                                  <div className="price-row">
                                    <span>Suplemento entrega a domicilio</span>
                                    <span>{formatEur(officialBr.deliveryCost)}</span>
                                  </div>
                                )}
                                {officialBr.discount > 0 && (
                                  <div className="price-row discount">
                                    <span>Descuento</span>
                                    <span>−{formatEur(officialBr.discount)}</span>
                                  </div>
                                )}
                                <div className="price-row total">
                                  <span>
                                    <strong>Total reserva</strong>
                                  </span>
                                  <span>
                                    <strong>{formatEur(officialBr.total)}</strong>
                                  </span>
                                </div>
                              </div>
                              {showPricePreviewSafe && (
                                <div
                                  className="edit-booking-price-breakdown-preview"
                                  aria-live="polite"
                                >
                                  <h4 className="edit-booking-price-breakdown-title">
                                    Estimación con cambios sin aplicar
                                  </h4>
                                  <ul className="edit-booking-preview-lines edit-booking-preview-lines--compact">
                                    <li>
                                      <span>Vehículos (líneas)</span>
                                      <span>{formatEur(previewBreakdown.vehicles)}</span>
                                    </li>
                                    <li>
                                      <span>Accesorios (borrador)</span>
                                      <span>{formatEur(previewBreakdown.accessories)}</span>
                                    </li>
                                    {(editForm.deliveryType === 'DELIVERY' ||
                                      editForm.deliveryType === 'HOTEL') && (
                                      <li>
                                        <span>Entrega</span>
                                        <span>{formatEur(previewBreakdown.delivery)}</span>
                                      </li>
                                    )}
                                    {previewBreakdown.discount > 0 && (
                                      <li>
                                        <span>Descuento</span>
                                        <span>−{formatEur(previewBreakdown.discount)}</span>
                                      </li>
                                    )}
                                    <li className="edit-booking-preview-total">
                                      <span>Total estimado</span>
                                      <strong>{formatEur(previewBreakdown.totalPreview)}</strong>
                                    </li>
                                  </ul>
                                  <p className="edit-form-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                                    Pulsa «Aplicar accesorios y entrega» (o la acción correspondiente) para fijar estos
                                    importes en el servidor.
                                  </p>
                                </div>
                              )}
                            </div>
                            <div
                              className={`edit-financial-grid${
                                refundPreviewIfApply > 0.02 ? ' edit-financial-grid--with-refund-preview' : ''
                              }`}
                            >
                              <div>
                                <span className="edit-financial-label">Total reserva</span>
                                <strong>{formatEur(total)}</strong>
                              </div>
                              <div>
                                <span className="edit-financial-label">Cobrado</span>
                                <strong>{formatEur(paid)}</strong>
                              </div>
                              <div>
                                <span className="edit-financial-label">Pendiente de cobro</span>
                                <strong
                                  className={dueDisplayed > 0.02 ? 'edit-financial-warn' : ''}
                                >
                                  {formatEur(dueDisplayed)}
                                </strong>
                                {draftEconomicDirty && (
                                  <span
                                    className="edit-form-hint"
                                    style={{ display: 'block', marginTop: 4, fontWeight: 400 }}
                                  >
                                    Estimado con borrador (total oficial en servidor: {formatEur(total)} hasta aplicar
                                    cambios).
                                  </span>
                                )}
                              </div>
                              {refundPreviewIfApply > 0.02 && (
                                <div className="edit-financial-preview-refund">
                                  <span className="edit-financial-label">
                                    A devolver al cliente (estim., tras aplicar cambios)
                                  </span>
                                  <strong className="edit-financial-preview-refund-amount">
                                    {formatEur(refundPreviewIfApply)}
                                  </strong>
                                  {creditDownLinesForRefundPreview.length > 0 ? (
                                    <ul className="edit-financial-preview-refund-breakdown">
                                      {creditDownLinesForRefundPreview.map((row) => (
                                        <li key={row.key}>
                                          <span className="edit-financial-preview-refund-breakdown-desc">
                                            {row.description}
                                          </span>
                                          <span className="edit-financial-preview-refund-breakdown-amt">
                                            −{formatEur(row.amountEur)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="edit-form-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                                      No hay líneas de accesorios o entrega en el borrador que expliquen esta bajada;
                                      revisa el desglose «Estimación con cambios sin aplicar» o otros cambios de precio
                                      pendientes.
                                    </p>
                                  )}
                                  <p className="edit-form-hint" style={{ marginTop: 6, marginBottom: 0 }}>
                                    Cobrado ({formatEur(paid)}) − total estimado ({formatEur(previewBreakdown.totalPreview)}
                                    ). Emite abono en Quipu tras aplicar accesorios/entrega.
                                  </p>
                                </div>
                              )}
                              {credit > 0 && (
                                <div className="edit-financial-credit">
                                  <span className="edit-financial-label">Importe a devolver / saldo a favor</span>
                                  <strong>{formatEur(credit)}</strong>
                                  {saldoFavorLedgerLines.length > 0 ? (
                                    <ul className="edit-financial-preview-refund-breakdown edit-financial-credit-breakdown">
                                      {saldoFavorLedgerLines.map((row) => (
                                        <li key={row.key}>
                                          <span className="edit-financial-preview-refund-breakdown-desc">
                                            {row.description}
                                          </span>
                                          <span className="edit-financial-preview-refund-breakdown-amt">
                                            −{formatEur(row.amountEur)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="edit-form-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                                      No hay detalle por concepto en el historial (reservas antiguas o ajustes sin
                                      desglose). Revisa la pestaña Historial o el desglose de precio guardado antes de
                                      emitir el abono en Quipu.
                                    </p>
                                  )}
                                  {user?.role !== 'AGENCY' && (
                                    <div className="edit-credit-abono-actions">
                                      <p className="edit-form-hint">
                                        Emite rectificativas en Quipu y registra el movimiento de devolución. Elige la
                                        forma de pago del abono al cliente; por criterio fiscal suele alinearse con el
                                        cobro de mayor importe.
                                      </p>
                                      {abonoHints.aggregated.length > 0 && (
                                        <div className="edit-credit-prior-wrap">
                                          <span className="edit-financial-label">Cobros previos por forma de pago</span>
                                          <ul className="edit-credit-prior-list">
                                            {abonoHints.aggregated.map((row) => (
                                              <li key={row.method}>
                                                <strong>{row.label}</strong>
                                                <span className="edit-credit-prior-amt">
                                                  {formatEur(row.totalEur)}
                                                </span>
                                                {abonoHints.recommended?.method === row.method ? (
                                                  <span className="edit-credit-prior-badge">Mayor cobro</span>
                                                ) : null}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      <div className="form-group edit-credit-abono-method-group">
                                        <label htmlFor="credit-abono-payment-method">
                                          Forma de pago del abono al cliente
                                        </label>
                                        <select
                                          id="credit-abono-payment-method"
                                          className="edit-credit-abono-select"
                                          value={creditAbonoPaymentMethod}
                                          onChange={(e) => setCreditAbonoPaymentMethod(e.target.value)}
                                        >
                                          {abonoOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                              {o.label}
                                            </option>
                                          ))}
                                        </select>
                                        <p className="edit-form-hint">
                                          {creditAbonoPaymentMethod === 'STRIPE' &&
                                            'Se intentará el reembolso parcial automático en Stripe (mismos cobros con tarjeta).'}
                                          {creditAbonoPaymentMethod === 'TPV' &&
                                            'Registra el abono como devolución por TPV; haz la operación en el datáfono si aplica.'}
                                          {(creditAbonoPaymentMethod === 'BIZUM' ||
                                            creditAbonoPaymentMethod === 'BANK_TRANSFER' ||
                                            creditAbonoPaymentMethod === 'CASH') &&
                                            'Quipu y el movimiento interno quedarán con esta forma de pago; realiza la devolución al cliente por ese canal.'}
                                          {creditAbonoPaymentMethod === 'NONE' &&
                                            'Solo contabilidad en Quipu y registro interno; el abono al cliente lo gestionas fuera del sistema.'}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn-secondary edit-credit-abono-btn"
                                        onClick={handleCreditAbonoQuipu}
                                        disabled={creditAbonoLoading}
                                      >
                                        {creditAbonoLoading ? (
                                          <>
                                            <Loader size={16} /> Procesando…
                                          </>
                                        ) : (
                                          <>
                                            <Receipt size={18} aria-hidden />
                                            Emitir abono en Quipu
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {!fin && (
                              <p className="edit-form-hint">No hay detalle financiero; cierra y vuelve a abrir la reserva.</p>
                            )}
                            {showPay && user?.role !== 'AGENCY' && (
                              <div className="edit-booking-payment-block">
                                <h4 className="edit-booking-payment-title">
                                  <CreditCard size={18} aria-hidden />
                                  Cobrar saldo pendiente
                                </h4>
                                <p className="edit-form-hint">
                                  Siguiente cobro esperado: <strong>{formatEur(payAmount)}</strong>
                                </p>
                                {fin && dueDisplayed > 0.02 && (
                                  <div className="edit-quipu-embed-in-payment">
                                    <h5 className="edit-booking-payment-subtitle">
                                      <FileText size={16} aria-hidden />
                                      {useDraftPendingSupplementLines
                                        ? 'Detalle estimado de la reserva (importe a cobrar)'
                                        : 'Desglose del importe a facturar'}
                                    </h5>
                                    <p className="edit-form-hint">
                                      Saldo pendiente: <strong>{formatEur(dueDisplayed)}</strong>
                                      {useDraftPendingSupplementLines
                                        ? ' (estimado con borrador de accesorios/entrega sin aplicar).'
                                        : '.'}{' '}
                                      {useDraftPendingSupplementLines
                                        ? 'La tabla siguiente muestra el detalle por bicicleta y concepto. La emisión en Quipu tras aplicar el borrador puede usar el reparto del servidor (buckets e histórico de cobros). '
                                        : 'La tabla siguiente repite el detalle por bicicleta y conceptos según los datos guardados en el servidor (coincide con lo que ya aplicaste). '}
                                      Puedes cobrar con tarjeta (Stripe) o registrar el cobro en tienda: en ese caso se
                                      pedirá confirmación para emitir la factura en Quipu y registrar el cobro con el{' '}
                                      <strong>mismo importe</strong>. Si cambias accesorios o entrega, aplica primero esos
                                      cambios más abajo para alinear el total oficial.
                                      {hasQuipuHistory && (
                                        <>
                                          {' '}
                                          <strong>Importante:</strong> si además debes rectificar importes ya facturados
                                          (abono), hazlo como operación aparte antes de mezclar con un suplemento nuevo.
                                        </>
                                      )}
                                    </p>
                                    {useDraftPendingSupplementLines && (
                                      <p className="edit-form-hint" style={{ marginTop: 8 }}>
                                        Incluye alquiler de vehículos, accesorios y entrega según el borrador actual. El
                                        importe inferior coincide con el saldo pendiente estimado (total estimado menos lo
                                        ya cobrado). Tras «Aplicar accesorios y entrega», Quipu usará el desglose
                                        definitivo del servidor.
                                      </p>
                                    )}
                                    {pendingSupplementPreviewLoading && (
                                      <p className="edit-form-hint" style={{ marginBottom: 8 }}>
                                        Actualizando vista previa fiscal del servidor…
                                      </p>
                                    )}
                                    <table className="edit-pending-supplement-table">
                                      <thead>
                                        <tr>
                                          <th>Concepto</th>
                                          <th className="edit-pending-supplement-amt">Importe</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {supplementBreakdownLines.map((row, idx) => (
                                          <tr key={`${row.concept}-${idx}`}>
                                            <td>{row.concept}</td>
                                            <td className="edit-pending-supplement-amt">{formatEur(row.amount)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot>
                                        <tr>
                                          <td>
                                            <strong>
                                              {useDraftPendingSupplementLines
                                                ? 'Saldo pendiente (estimado)'
                                                : 'Saldo pendiente'}
                                            </strong>
                                          </td>
                                          <td className="edit-pending-supplement-amt">
                                            <strong>{formatEur(supplementBreakdownFooterAmount)}</strong>
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                    {quipuFiscalPreview && quipuFiscalPreview.lines.length > 0 && (
                                      <div className="edit-quipu-fiscal-supplement-block" style={{ marginTop: 16 }}>
                                        <h5 className="edit-booking-payment-subtitle">
                                          <Receipt size={16} aria-hidden />
                                          {useDraftPendingSupplementLines
                                            ? 'Factura en Quipu (suplemento — solo incrementos)'
                                            : 'Factura en Quipu (imputación del saldo pendiente)'}
                                        </h5>
                                        <p className="edit-form-hint" style={{ marginBottom: 8 }}>
                                          {useDraftPendingSupplementLines ? (
                                            <>
                                              Orientación fiscal: líneas que corresponden a <strong>lo añadido</strong>{' '}
                                              frente a lo ya guardado en servidor (entrega, alquiler pendiente de ese
                                              importe, incrementos de accesorios y seguros). Coincide en total con el
                                              importe a facturar indicado arriba; la emisión definitiva en el servidor
                                              puede ajustar el reparto si ya hubo cobros imputados o reglas de ledger.
                                            </>
                                          ) : (
                                            <>
                                              Reparto previsto del importe a facturar en Quipu (orden: entrega, alquiler,
                                              seguros, accesorios según lo que quede pendiente en cada concepto). Puede
                                              diferir del detalle por bicicleta de la tabla superior, que es el desglose
                                              comercial de la reserva.
                                            </>
                                          )}
                                        </p>
                                        <table className="edit-pending-supplement-table edit-quipu-fiscal-supplement-table">
                                          <thead>
                                            <tr>
                                              <th>Concepto (Quipu)</th>
                                              <th className="edit-pending-supplement-amt">Importe</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {quipuFiscalPreview.lines.map((row, idx) => (
                                              <tr key={`quipu-fiscal-${row.concept}-${idx}`}>
                                                <td>{row.concept}</td>
                                                <td className="edit-pending-supplement-amt">
                                                  {formatEur(row.amount)}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                          <tfoot>
                                            <tr>
                                              <td>
                                                <strong>Total factura (objetivo)</strong>
                                              </td>
                                              <td className="edit-pending-supplement-amt">
                                                <strong>{formatEur(amountForQuipuOrientative)}</strong>
                                              </td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    )}
                                    <label className="edit-partial-supplement-toggle">
                                      <input
                                        type="checkbox"
                                        checked={showPartialSupplementAmount}
                                        onChange={(e) => {
                                          setShowPartialSupplementAmount(e.target.checked);
                                          if (!e.target.checked) setSupplementQuipuAmount('');
                                        }}
                                      />
                                      Facturar solo parte del pendiente (importe distinto)
                                    </label>
                                    {showPartialSupplementAmount && (
                                      <div className="form-group" style={{ marginTop: '10px' }}>
                                        <label htmlFor="supplement-quipu-amount-payment">Importe a facturar (€)</label>
                                        <input
                                          id="supplement-quipu-amount-payment"
                                          type="text"
                                          inputMode="decimal"
                                          placeholder={`Máx. ${dueDisplayed.toFixed(2)}`}
                                          value={supplementQuipuAmount}
                                          onChange={(e) => setSupplementQuipuAmount(e.target.value)}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="edit-payment-after-quipu-divider">Cobro del saldo</div>
                                {draftEconomicDirty && (
                                  <p className="edit-form-hint" style={{ color: '#b91c1c' }}>
                                    Tienes cambios de accesorios o entrega sin aplicar. Pulsa «Aplicar accesorios y
                                    entrega» (o «Aplicar y emitir factura Quipu») antes de cobrar, para que el importe
                                    coincida con el total guardado en el servidor.
                                  </p>
                                )}
                                {customerEmail && !draftEconomicDirty ? (
                                  <EditBookingBalanceStripe
                                    enabled
                                    bookingId={selectedBooking.id}
                                    amountEur={payAmount}
                                    customerEmail={customerEmail}
                                    customerName={customerName}
                                    onSuccess={() => handleEditBooking({ id: selectedBooking.id })}
                                  />
                                ) : !draftEconomicDirty ? (
                                  <p className="edit-form-hint">Falta email del cliente para cobrar con tarjeta (Stripe).</p>
                                ) : null}
                                <div className="edit-offline-divider">Registrar cobro en tienda</div>
                                <div className="form-row">
                                  <div className="form-group">
                                    <label>Método</label>
                                    <select
                                      value={offlinePayMethod}
                                      onChange={(e) => setOfflinePayMethod(e.target.value)}
                                    >
                                      <option value="CASH">Efectivo</option>
                                      <option value="BIZUM">Bizum</option>
                                      <option value="BANK_TRANSFER">Transferencia</option>
                                      <option value="TPV">TPV datáfono</option>
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label>Importe a registrar</label>
                                    <input type="text" readOnly value={formatEur(payAmount)} />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={openConfirmManualPayWithQuipu}
                                  disabled={
                                    confirmManualPayLoading ||
                                    savingBikeLines ||
                                    savingDeliveryApply ||
                                    savingApplyLinesAndDelivery ||
                                    savingApplyLinesAndEmitQuipu ||
                                    !hasSupplementBreakdownForManualPay ||
                                    payAmount <= 0 ||
                                    draftEconomicDirty
                                  }
                                >
                                  Registrar cobro manual
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {!isBikeFleetBooking && (
                        <div className="form-row">
                          <div className="form-group">
                            <label>Fecha inicio</label>
                            <input type="text" readOnly value={formatDateTime(selectedBooking.startDateTime)} />
                          </div>
                          <div className="form-group">
                            <label>Fecha fin</label>
                            <input type="text" readOnly value={formatDateTime(selectedBooking.endDateTime)} />
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeEditModal}
                  >
                    Cerrar
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleRequestSaveBooking}
                    disabled={saving}
                  >
                    {saving ? <Loader size={16} /> : <Save size={16} />}
                    {saving ? 'Guardando...' : 'Guardar datos de cliente y estado'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

      {showConfirmApplyLinesAndDelivery && (
        <div className="modal-overlay modal-overlay-stacked">
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h2>Confirmar cambios de importe</h2>
              <button type="button" onClick={() => setShowConfirmApplyLinesAndDelivery(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p>
                Se guardarán en el servidor los accesorios por bicicleta (si hay líneas) y el tipo de entrega / suplemento.
                El total oficial de la reserva y el pendiente se actualizarán; después podrás facturar en Quipu si
                corresponde.
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowConfirmApplyLinesAndDelivery(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirmApplyLinesAndDelivery}
                  disabled={savingApplyLinesAndDelivery}
                >
                  {savingApplyLinesAndDelivery ? <Loader size={16} /> : null}
                  {savingApplyLinesAndDelivery ? 'Aplicando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmApplyLinesAndEmitQuipu && (
        <div className="modal-overlay modal-overlay-stacked">
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h2>Aplicar, actualizar y emitir en Quipu</h2>
              <button type="button" onClick={() => setShowConfirmApplyLinesAndEmitQuipu(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p>
                Se guardarán las líneas de accesorios (si hay bicicletas en la reserva), después los datos de entrega en
                el servidor y, si queda saldo pendiente, se emitirá en Quipu la factura con desglose automático
                (entrega, seguros, accesorios, alquiler).
              </p>
              <p className="edit-form-hint">
                Si ya existe una factura Quipu pendiente de cobro en esta reserva, la operación fallará hasta que la
                liquides o canceles ese cobro pendiente.
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowConfirmApplyLinesAndEmitQuipu(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConfirmApplyLinesAndEmitQuipu}
                  disabled={savingApplyLinesAndEmitQuipu}
                >
                  {savingApplyLinesAndEmitQuipu ? <Loader size={16} /> : null}
                  {savingApplyLinesAndEmitQuipu ? 'Procesando…' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmManualPayWithQuipuModal && selectedBooking && (() => {
        const payResolve = resolvePendingSupplementPayAmountEur();
        const amountForModal = payResolve.amountEur;
        const draftExtraModal = getPendingSupplementDraftExtraLines(
          selectedBooking,
          editForm,
          accessoryDraftByRentalId
        );
        const quipuModalLines =
          !payResolve.error && amountForModal != null
            ? draftExtraModal.hasUnsavedEconomicDraft
              ? computeQuipuSupplementOrientativeLines(
                  selectedBooking,
                  editForm,
                  accessoryDraftByRentalId,
                  amountForModal,
                  resolveDraftAccessoryCategory
                ).lines
              : pendingSupplementPreview?.lines || []
            : [];
        const offlineMethodLabels = {
          CASH: 'Efectivo',
          BIZUM: 'Bizum',
          BANK_TRANSFER: 'Transferencia',
          TPV: 'TPV datáfono',
        };
        const methodLabel = offlineMethodLabels[offlinePayMethod] || offlinePayMethod;
        return (
          <div className="modal-overlay modal-overlay-stacked">
            <div className="modal modal-confirm">
              <div className="modal-header">
                <h2>Confirmar cobro en tienda</h2>
                <button
                  type="button"
                  onClick={() => !confirmManualPayLoading && setShowConfirmManualPayWithQuipuModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="modal-content">
                {payResolve.error ? (
                  <p className="edit-form-hint">{payResolve.error}</p>
                ) : (
                  <>
                    <p>
                      Vas a crear la <strong>factura en Quipu</strong> por{' '}
                      <strong>{formatEur(amountForModal)}</strong> (IVA incluido) y a continuación{' '}
                      <strong>registrar el cobro</strong> en el sistema por el <strong>mismo importe</strong> como{' '}
                      <strong>{methodLabel}</strong>.
                    </p>
                    {quipuModalLines.length > 0 ? (
                      <>
                        <p className="edit-form-hint" style={{ marginBottom: 8 }}>
                          {draftExtraModal.hasUnsavedEconomicDirty
                            ? 'Conceptos orientativos del suplemento (incrementos respecto a lo guardado). El backend puede refinar el reparto al emitir.'
                            : 'Conceptos que se enviarán a Quipu para esta factura:'}
                        </p>
                        <table className="edit-pending-supplement-table" style={{ marginBottom: 12 }}>
                          <thead>
                            <tr>
                              <th>Concepto</th>
                              <th className="edit-pending-supplement-amt">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quipuModalLines.map((row, idx) => (
                              <tr key={`modal-quipu-${row.concept}-${idx}`}>
                                <td>{row.concept}</td>
                                <td className="edit-pending-supplement-amt">{formatEur(row.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td>
                                <strong>Total</strong>
                              </td>
                              <td className="edit-pending-supplement-amt">
                                <strong>{formatEur(amountForModal)}</strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </>
                    ) : (
                      <p className="edit-form-hint">
                        No hay líneas de desglose en caché; el servidor generará las líneas al emitir.
                      </p>
                    )}
                    <p className="edit-form-hint">
                      Si ya hubiera una factura Quipu pendiente del mismo importe para esta reserva, solo se registrará el
                      cobro y se liquidará esa factura.
                    </p>
                  </>
                )}
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowConfirmManualPayWithQuipuModal(false)}
                    disabled={confirmManualPayLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleConfirmManualPayWithQuipu}
                    disabled={confirmManualPayLoading || !!payResolve.error}
                  >
                    {confirmManualPayLoading ? <Loader size={16} /> : null}
                    {confirmManualPayLoading ? 'Procesando…' : 'Confirmar factura y cobro'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {postApplyFinancialModal && (
        <div className="modal-overlay modal-overlay-stacked">
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h2>
                {postApplyFinancialModal.kind === 'supplement'
                  ? 'Pendiente actualizado'
                  : 'Importe pendiente reducido'}
              </h2>
              <button type="button" onClick={() => setPostApplyFinancialModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              {postApplyFinancialModal.kind === 'supplement' && (
                <>
                  {postApplyFinancialModal.breakdown && (
                    <div className="edit-post-apply-breakdown">
                      <h4 className="edit-post-apply-breakdown-title">Desglose del cambio (servidor)</h4>
                      <table className="edit-post-apply-breakdown-table">
                        <tbody>
                          {Math.abs(postApplyFinancialModal.breakdown.deltaVehicles) > 0.005 && (
                            <tr>
                              <td>Alquiler bicicletas</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaVehicles >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaVehicles))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaAccessories) > 0.005 && (
                            <tr>
                              <td>Accesorios (total)</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaAccessories >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaAccessories))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaInsurance) > 0.005 && (
                            <tr>
                              <td>Seguros</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaInsurance >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaInsurance))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaDelivery) > 0.005 && (
                            <tr>
                              <td>Entrega a domicilio</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaDelivery >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaDelivery))}
                              </td>
                            </tr>
                          )}
                          <tr className="edit-post-apply-breakdown-total">
                            <td>
                              <strong>Δ Total reserva</strong>
                            </td>
                            <td className="edit-post-apply-breakdown-amt">
                              {postApplyFinancialModal.breakdown.deltaTotal >= 0 ? '+' : '−'}
                              {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaTotal))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {postApplyFinancialModal.fiscalSplitRequired && (
                    <div className="edit-post-apply-fiscal-warn">
                      <p>
                        <strong>Facturación: dos operaciones distintas.</strong> Hay cobros ya facturados en Quipu y, en
                        este cambio, <strong>bajan y suben</strong> conceptos a la vez. No conviene emitir{' '}
                        <strong>una sola</strong> factura de suplemento por el importe neto del pendiente: primero hay que
                        <strong> rectificar</strong> lo que deja de aplicarse (p. ej. accesorio ya facturado) y{' '}
                        <strong>después</strong> facturar aparte lo nuevo (p. ej. entrega).
                      </p>
                      <ol className="edit-post-apply-fiscal-steps">
                        <li>
                          <strong>Abono / rectificativa (bajada):</strong> en la pestaña «Cobros y facturación», usa «Emitir
                          abono en Quipu» si ya aparece saldo a favor coherente con la baja. Si no aplica el asistente,
                          emite la rectificativa en Quipu por el importe de lo dejado de cobrar (accesorios/seguros según
                          tu caso).
                        </li>
                        <li>
                          <strong>Suplemento (subida):</strong> factura aparte el importe del nuevo concepto (p. ej. solo
                          entrega a domicilio) desde la pestaña «Cobros y facturación» (usa «Ver detalle» al cerrar este
                          aviso).
                        </li>
                      </ol>
                      {!(postApplyFinancialModal.breakdown?.deltaDelivery > 0.02) && (
                        <p className="edit-form-hint" style={{ marginTop: 10 }}>
                          No hay subida de entrega a domicilio en este cambio: emite rectificativas y nuevas facturas según
                          cada concepto desde la pestaña «Cobros y facturación».
                        </p>
                      )}
                    </div>
                  )}

                  <p>
                    El pendiente ha subido <strong>{formatEur(postApplyFinancialModal.suggestedAmount)}</strong> (de{' '}
                    {formatEur(postApplyFinancialModal.oldDue)} a {formatEur(postApplyFinancialModal.newDue)}).
                  </p>
                  {!postApplyFinancialModal.fiscalSplitRequired && (
                    <p className="edit-form-hint">
                      Pulsa <strong>Ver detalle</strong> para ir a la pestaña «Cobros y facturación»: allí verás el
                      desglose, podrás emitir en Quipu y registrar el cobro (p. ej. «Registrar cobro manual»).
                    </p>
                  )}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setPostApplyFinancialModal(null)}
                    >
                      Cerrar
                    </button>
                    {!postApplyFinancialModal.fiscalSplitRequired && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                          setPostApplyFinancialModal(null);
                          setEditModalTab('FINANCIAL');
                        }}
                      >
                        Ver detalle
                      </button>
                    )}
                    {postApplyFinancialModal.fiscalSplitRequired &&
                      postApplyFinancialModal.breakdown &&
                      postApplyFinancialModal.breakdown.deltaDelivery > 0.02 && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => {
                            setPostApplyFinancialModal(null);
                            setEditModalTab('FINANCIAL');
                          }}
                        >
                          Ver detalle
                        </button>
                      )}
                  </div>
                </>
              )}
              {postApplyFinancialModal.kind === 'due_down' && (
                <>
                  {postApplyFinancialModal.breakdown && (
                    <div className="edit-post-apply-breakdown">
                      <h4 className="edit-post-apply-breakdown-title">Desglose del cambio (servidor)</h4>
                      <table className="edit-post-apply-breakdown-table">
                        <tbody>
                          {Math.abs(postApplyFinancialModal.breakdown.deltaVehicles) > 0.005 && (
                            <tr>
                              <td>Alquiler bicicletas</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaVehicles >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaVehicles))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaAccessories) > 0.005 && (
                            <tr>
                              <td>Accesorios (total)</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaAccessories >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaAccessories))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaInsurance) > 0.005 && (
                            <tr>
                              <td>Seguros</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaInsurance >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaInsurance))}
                              </td>
                            </tr>
                          )}
                          {Math.abs(postApplyFinancialModal.breakdown.deltaDelivery) > 0.005 && (
                            <tr>
                              <td>Entrega a domicilio</td>
                              <td className="edit-post-apply-breakdown-amt">
                                {postApplyFinancialModal.breakdown.deltaDelivery >= 0 ? '+' : '−'}
                                {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaDelivery))}
                              </td>
                            </tr>
                          )}
                          <tr className="edit-post-apply-breakdown-total">
                            <td>
                              <strong>Δ Total reserva</strong>
                            </td>
                            <td className="edit-post-apply-breakdown-amt">
                              {postApplyFinancialModal.breakdown.deltaTotal >= 0 ? '+' : '−'}
                              {formatEur(Math.abs(postApplyFinancialModal.breakdown.deltaTotal))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {postApplyFinancialModal.fiscalSplitRequired && (
                    <div className="edit-post-apply-fiscal-warn">
                      <p>
                        <strong>Atención fiscal:</strong> hay facturas Quipu previas y el cambio mezcla bajadas y subidas.
                        Valida rectificativas y nuevas facturas por separado desde «Cobros y facturación».
                      </p>
                    </div>
                  )}
                  <p>
                    El pendiente ha bajado de {formatEur(postApplyFinancialModal.oldDue)} a{' '}
                    {formatEur(postApplyFinancialModal.newDue)}.
                  </p>
                  {postApplyFinancialModal.creditHint ? (
                    <p className="edit-form-hint">
                      Puede haber saldo a favor del cliente. Pulsa <strong>Ver detalle</strong> para ir a la pestaña{' '}
                      <strong>Cobros y facturación</strong> y usar «Emitir abono en Quipu» si corresponde rectificar o
                      devolver.
                    </p>
                  ) : (
                    <p className="edit-form-hint">
                      Pulsa <strong>Ver detalle</strong> para ir a la pestaña «Cobros y facturación» y gestionar
                      rectificaciones o cobros si aplica.
                    </p>
                  )}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setPostApplyFinancialModal(null)}
                    >
                      Cerrar
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setPostApplyFinancialModal(null);
                        setEditModalTab('FINANCIAL');
                      }}
                    >
                      Ver detalle
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

        </Fragment>
      )}

      {/* Modal de confirmación antes de guardar cambios */}
      {showConfirmSaveModal && (
        <div className="modal-overlay">
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h2>Guardar datos de cliente y estado</h2>
              <button onClick={() => setShowConfirmSaveModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p>
                Se guardarán cliente, estado de la reserva, notas y estado de abono (si aplica). Los importes de
                accesorios, entrega, fechas o bicicletas no se modifican desde este paso.
              </p>
              {editForm.status === 'CANCELLED' && selectedBooking?.status !== 'CANCELLED' && isStripePayment(selectedBooking) && (
                <p className="edit-form-hint">Se realizará el reembolso del 100% del importe en Stripe.</p>
              )}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowConfirmSaveModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn-primary" onClick={handleSaveBooking} disabled={saving}>
                  {saving ? <Loader size={16} /> : null}
                  {saving ? 'Guardando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de cambio de talla al reemplazar bici */}
      {showConfirmBikeSizeChangeModal && pendingBikeSizeChange && (
        <div className="modal-overlay">
          <div className="modal modal-confirm">
            <div className="modal-header">
              <h2>Confirmar cambio de talla</h2>
              <button
                onClick={() => {
                  setShowConfirmBikeSizeChangeModal(false);
                  setPendingBikeSizeChange(null);
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p>
                La bicicleta seleccionada tiene una talla distinta ({pendingBikeSizeChange.currentSize} → {pendingBikeSizeChange.newSize}).
              </p>
              <p className="edit-form-hint">
                ¿Quieres aplicar igualmente el cambio de bicicleta?
              </p>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowConfirmBikeSizeChangeModal(false);
                    setPendingBikeSizeChange(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    const { bookingId, rentalId } = pendingBikeSizeChange;
                    setShowConfirmBikeSizeChangeModal(false);
                    setPendingBikeSizeChange(null);
                    applyReplacementVehicle(bookingId, rentalId);
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de eliminación definitiva de reserva (solo ADMIN) */}
      {deleteTarget && (() => {
        const preview = deleteTarget.preview;
        const confirmWord = preview?.booking?.bookingReference || preview?.booking?.id || '';
        const canConfirm = !!preview && deleteConfirmText.trim() === String(confirmWord);
        return (
          <div className="modal-overlay">
            <div className="modal modal-confirm delete-booking-modal">
              <div className="modal-header">
                <h2>Eliminar reserva #{preview?.booking?.bookingReference || deleteTarget.bookingId}</h2>
                <button onClick={closeDeleteBookingModal} disabled={deletingBooking}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-content">
                <div className="delete-booking-alert" role="alert">
                  <strong>⚠️ Esta acción es irreversible.</strong>
                  <span>
                    Los datos se borrarán definitivamente de la base de datos y no se podrán recuperar.
                  </span>
                </div>

                {deleteTarget.loading && (
                  <p className="delete-booking-loading"><Loader size={16} /> Cargando información de la reserva…</p>
                )}

                {preview && (
                  <>
                    <p>Se eliminará lo siguiente:</p>
                    <ul className="delete-booking-list">
                      <li>
                        <strong>Reserva</strong> {preview.booking.bookingReference || preview.booking.id} ·{' '}
                        {getStatusBadge(preview.booking.status)} · €{preview.booking.total} ·{' '}
                        {formatDateOnly(preview.booking.startDateTime)} → {formatDateOnly(preview.booking.endDateTime)}
                      </li>
                      {preview.customer && (
                        <li>
                          <strong>Cliente</strong> {preview.customer.fullName} ({preview.customer.email})
                          {!preview.customer.willBeDeleted && (
                            <div className="delete-booking-note">
                              No se eliminará: tiene {preview.customer.otherBookings} reserva(s) más u otros pagos asociados.
                            </div>
                          )}
                        </li>
                      )}
                      <li>
                        <strong>Contratos</strong>{' '}
                        {preview.contracts.length > 0
                          ? `${preview.contracts.length} (PDF y firmas incluidos)`
                          : 'ninguno'}
                      </li>
                      {preview.vehicleRentals.length > 0 && (
                        <li>
                          <strong>Bicicletas/vehículos asignados</strong> {preview.vehicleRentals.length}:{' '}
                          {preview.vehicleRentals
                            .map((r) => `${r.label}${r.serialNumber ? ` (${r.serialNumber})` : ''}`)
                            .join(', ')}
                          {preview.vehicleRentals.some((r) => r.accessories > 0) && ' · con sus accesorios'}
                        </li>
                      )}
                      {preview.counts.availabilityBlocks > 0 && (
                        <li>
                          <strong>Bloqueos de disponibilidad</strong> {preview.counts.availabilityBlocks} (las bicis
                          quedarán libres en esas fechas)
                        </li>
                      )}
                      {preview.payments.some((p) => !p.invoiced) && (
                        <li>
                          <strong>Pagos sin factura</strong>{' '}
                          {preview.payments
                            .filter((p) => !p.invoiced)
                            .map((p) => `€${p.grossAmount} ${p.paymentMethod} (${p.status})`)
                            .join(', ')}
                        </li>
                      )}
                      {preview.counts.ledgerEntries > 0 && (
                        <li><strong>Historial de movimientos</strong> {preview.counts.ledgerEntries} entradas</li>
                      )}
                      {preview.counts.usageHistory > 0 && (
                        <li><strong>Historial de uso</strong> {preview.counts.usageHistory} registros</li>
                      )}
                    </ul>

                    {(preview.warnings.hasInvoicedPayments ||
                      preview.warnings.hasPaidStripePayments ||
                      preview.warnings.isActivityBooking) && (
                      <div className="delete-booking-alert delete-booking-alert--soft">
                        {preview.warnings.hasInvoicedPayments && (
                          <span>
                            Se <strong>conservan</strong> {preview.payments.filter((p) => p.invoiced).length} pago(s) facturado(s) (
                            {preview.payments.filter((p) => p.invoiced).map((p) => p.quipuInvoiceNumber || `€${p.grossAmount}`).join(', ')}
                            ) desvinculados de la reserva, para que la contabilidad siga cuadrando. Las facturas{' '}
                            <strong>no se anulan en Quipu</strong>: si procede, emite la rectificativa manualmente.
                          </span>
                        )}
                        {preview.warnings.hasPaidStripePayments && (
                          <span>
                            Hay cobros de Stripe pagados. <strong>No se reembolsan automáticamente</strong>; si quieres
                            devolver el dinero, cancela la reserva antes de eliminarla.
                          </span>
                        )}
                        {preview.warnings.isActivityBooking && (
                          <span>
                            Es una reserva de actividad: las plazas consumidas del horario no se liberan automáticamente.
                          </span>
                        )}
                      </div>
                    )}

                    <label className="delete-booking-confirm-label">
                      Para confirmar, escribe <code>{confirmWord}</code>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        autoFocus
                        disabled={deletingBooking}
                      />
                    </label>
                  </>
                )}

                {deleteTarget.error && <p className="delete-booking-error">{deleteTarget.error}</p>}

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeDeleteBookingModal} disabled={deletingBooking}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleConfirmDeleteBooking}
                    disabled={!canConfirm || deletingBooking}
                  >
                    {deletingBooking ? <Loader size={16} /> : <Trash2 size={16} />}
                    {deletingBooking ? 'Eliminando...' : 'Eliminar definitivamente'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Creación */}
      {showCreateModal && (
        <CrearReserva
          key={createReservaModalKey}
          prefillCustomer={createReservaCustomerPrefill}
          onClose={() => {
            setShowCreateModal(false);
            setCreateReservaCustomerPrefill(null);
          }}
          onSuccess={handleBookingCreated}
          onBookingCreated={handleBookingCreated}
        />
      )}
    </div>
  );
};

export default Reservas; 